import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Search, Plus, Cat, Heart, Stethoscope, Calendar, AlertTriangle, Edit2, UserPlus, X, Upload, Star, Trash2, Image as ImageIcon, Loader2, Building2, Camera, Leaf, Building, Users, User, RotateCw, RotateCcw, ZoomIn, Maximize2, Crop, Check, HelpCircle, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, MessageSquare, Eye, AlertCircle, StickyNote, List, ArrowLeft, Rss, Play, Share2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { ShareNotificationButton } from '../components/ShareNotificationButton';
import { UploadMediaModal, EditMediaModal, ImageSettingsModal } from './Gallery';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const addCacheBust = (url, ts) => {
  if (!ts || !url) return url || '';
  return url.includes('?') ? `${url}&t=${ts}` : `${url}?t=${ts}`;
};

/** Timestamp de cache-bust : session > updated_at persistant > 0 */
const mediaBust = (media, sessionBust) => {
  if (sessionBust) return sessionBust;
  if (media?.updated_at) return new Date(media.updated_at).getTime();
  return 0;
};

const mediaViewerUrl  = (media, sessionBust) =>
  addCacheBust(resolveUrl(media?.variants?.screen?.url || media?.file_url || ''), mediaBust(media, sessionBust));

const mediaThumbUrl = (media, sessionBust) =>
  addCacheBust(resolveUrl(media?.variants?.thumb?.url || media?.thumbnail_url || media?.file_url || ''), mediaBust(media, sessionBust));

/** Vignette individuelle avec gestion autonome des images cassées */
const HorseMediaThumb = ({ media, index, cacheBust, onOpen }) => {
  const [broken, setBroken] = useState(false);
  const isVideo = media.media_type === 'video';
  const thumbSrc = mediaThumbUrl(media, cacheBust);

  // Image introuvable → masquer silencieusement (sans toucher à la base)
  if (broken) return null;

  return (
    <div
      className="aspect-square relative cursor-pointer overflow-hidden"
      style={{ backgroundColor: media.dominant_color || '#e7e5e4' }}
      onClick={() => onOpen(index)}
    >
      {(media.status === 'uploading' || media.status === 'processing') && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/20 z-10">
          <Loader2 className="w-5 h-5 text-white animate-spin" />
          <span className="text-xs text-white mt-1">
            {media.processing_progress ? `${media.processing_progress}%` : '...'}
          </span>
        </div>
      )}
      {isVideo ? (
        <div className="relative w-full h-full">
          <video
            src={media.file_url?.startsWith('http') ? `${media.file_url}#t=0.5` : `${API_URL}${media.file_url}#t=0.5`}
            className="w-full h-full object-cover"
            muted playsInline preload="metadata"
          />
          <div className="absolute bottom-1 left-1">
            <Play className="w-4 h-4 text-white drop-shadow-md fill-white" />
          </div>
        </div>
      ) : (
        <img
          src={thumbSrc}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      )}
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de commentaires
const commentTypes = [
  { value: 'attention', label: 'Attention !', icon: AlertTriangle, color: 'bg-red-100 text-red-800 border-red-300', iconColor: 'text-red-600' },
  { value: 'a_surveiller', label: 'À surveiller', icon: Eye, color: 'bg-orange-100 text-orange-800 border-orange-300', iconColor: 'text-orange-600' },
  { value: 'note', label: 'Note', icon: StickyNote, color: 'bg-blue-100 text-blue-800 border-blue-300', iconColor: 'text-blue-600' },
];

const pensionTypes = [
  { value: 'box_poney', label: 'Box poney', tarif: 325 },
  { value: 'box_cheval_paille', label: 'Box cheval paille', tarif: 390 },
  { value: 'box_cheval_copeaux', label: 'Box cheval copeaux', tarif: 430 },
  { value: 'pre', label: 'Pré', tarif: 245 },
];

const horseStatuses = [
  { value: 'actif', label: 'Actif', color: 'bg-green-100 text-green-800' },
  { value: 'blesse', label: 'Blessé', color: 'bg-red-100 text-red-800' },
  { value: 'retraite', label: 'Retraité', color: 'bg-amber-100 text-amber-800' },
  { value: 'vendu', label: 'Vendu', color: 'bg-stone-100 text-stone-800' },
];

const niveauPerformanceOptions = [
  { value: 'debutant', label: 'Débutant', color: 'bg-blue-100 text-blue-800' },
  { value: 'club', label: 'Club', color: 'bg-green-100 text-green-800' },
  { value: 'amateur', label: 'Amateur', color: 'bg-amber-100 text-amber-800' },
  { value: 'pro', label: 'Pro', color: 'bg-purple-100 text-purple-800' },
];

// Fréquences de soins
const frequencesSemaines = [
  { value: 0, label: 'Indéterminée' },
  { value: 6, label: '6 semaines' },
];

const frequencesMois = [
  { value: 0, label: 'Indéterminée' },
  { value: 1, label: '1 mois' },
  { value: 3, label: '3 mois' },
  { value: 6, label: '6 mois' },
  { value: 12, label: '12 mois' },
];

// Helper pour calculer la prochaine date
const calculateNextDate = (derniere, freqSemaines, freqMois) => {
  if (!derniere) return null;
  const date = new Date(derniere);
  if (freqSemaines && freqSemaines > 0) {
    date.setDate(date.getDate() + freqSemaines * 7);
  } else if (freqMois && freqMois > 0) {
    date.setMonth(date.getMonth() + freqMois);
  } else {
    return null;
  }
  return date;
};

// Helper pour le statut du soin
const getCareStatus = (nextDate) => {
  if (!nextDate) return { status: 'unknown', label: 'Non défini', color: 'text-gray-500' };
  const today = new Date();
  const diffDays = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24));
  if (diffDays < -7) return { status: 'overdue', label: 'En retard', color: 'text-red-600 font-bold' };
  if (diffDays < 0) return { status: 'late', label: 'À faire', color: 'text-orange-600 font-semibold' };
  if (diffDays <= 14) return { status: 'soon', label: 'Bientôt', color: 'text-amber-600' };
  return { status: 'ok', label: 'OK', color: 'text-emerald-600' };
};

export const Horses = () => {
  const { isInstructor, isAdmin, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [horses, setHorses] = useState([]);
  const [cavaliers, setCavaliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('actif');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [editingSection, setEditingSection] = useState(null); // 'general', 'pension', 'cavaliers', 'medical', 'alimentation', 'photos'
  const [horseRiders, setHorseRiders] = useState(null);
  const [assignMode, setAssignMode] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [horseToDelete, setHorseToDelete] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [deletedHorses, setDeletedHorses] = useState([]);
  // (ancien système photos[] supprimé — unification vers media table)
  // État pour le modal d'upload de médias
  const [mediaUploadOpen, setMediaUploadOpen] = useState(false);
  // États pour la galerie du cheval (style page galerie)
  const [horseMedias, setHorseMedias] = useState([]);
  const [horseGalleryLoading, setHorseGalleryLoading] = useState(false);
  const [horseGalleryZoom, setHorseGalleryZoom] = useState(4);
  const [horseViewerIndex, setHorseViewerIndex] = useState(null);
  const [horseEditModalOpen, setHorseEditModalOpen] = useState(false);
  const [mediaToEdit, setMediaToEdit] = useState(null);
  // New: Settings modal for image adjustments
  const [horseSettingsModalOpen, setHorseSettingsModalOpen] = useState(false);
  const [mediaToAdjust, setMediaToAdjust] = useState(null);
  // Viewer inline editing
  const [horseViewerRotation, setHorseViewerRotation] = useState(0);
  const [horseViewerCropMode, setHorseViewerCropMode] = useState(false);
  const [horseViewerCrop, setHorseViewerCrop] = useState(undefined);
  const [horseViewerCompletedCrop, setHorseViewerCompletedCrop] = useState(null);
  const [horseViewerPendingCrop, setHorseViewerPendingCrop] = useState(null);
  const [horseViewerApplying, setHorseViewerApplying] = useState(false);
  const [horseViewerCacheBust, setHorseViewerCacheBust] = useState(0);
  const [horseGridCacheBust, setHorseGridCacheBust] = useState(0);
  const horseViewerImgRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // États pour les cartes pliables (toutes fermées par défaut sauf Santé & Soins)
  const [collapsedCards, setCollapsedCards] = useState({
    general: true,
    photos: false,  // Galerie ouverte par défaut
    medical: false,  // Ouverte par défaut
    alimentation: true,
    cavaliers: true,
    commentaires: false,
    pension: true
  });
  
  // États pour les commentaires
  const [newComment, setNewComment] = useState({ type: 'note', content: '' });
  const [addingComment, setAddingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null); // Prevent double-click on delete
  const [validationModal, setValidationModal] = useState(null); // { commentaire, horse }
  const [commentFilter, setCommentFilter] = useState('all'); // 'all', 'attention', 'a_surveiller', 'note'
  const [commentPanelType, setCommentPanelType] = useState(null); // null, 'attention', 'a_surveiller', 'note' - pour le panneau plein écran
  
  // Toggle card collapse
  const toggleCard = (cardName) => {
    setCollapsedCards(prev => ({ ...prev, [cardName]: !prev[cardName] }));
  };
  
  // Check if current user can edit a specific horse (owner, PP or instructor)
  const canEditHorse = (horse) => {
    if (!horse || !user) return false;
    if (isInstructor()) return true;
    // Propriétaire : accès complet sauf suppression et modification du propriétaire
    if (horse.proprietaire_id === user.id) return true;
    // PP : mêmes droits que propriétaire (sauf cavaliers associés)
    if (horse.proprietaires_partages?.includes(user.id)) return true;
    // DP : mêmes droits que propriétaire (sauf cavaliers associés)
    if (horse.demi_pensionnaires?.includes(user.id)) return true;
    return false;
  };

  // Check if user is the owner (propriétaire) or PP (propriétaire partagé) or DP
  const isOwnerOrPPOrDP = (horse) => {
    if (!horse || !user) return false;
    return horse.proprietaire_id === user.id || 
           horse.proprietaires_partages?.includes(user.id) ||
           horse.demi_pensionnaires?.includes(user.id);
  };

  // Check if user is the owner (propriétaire) or PP (propriétaire partagé) - for backward compatibility
  const isOwnerOrPP = (horse) => {
    if (!horse || !user) return false;
    return horse.proprietaire_id === user.id || horse.proprietaires_partages?.includes(user.id);
  };

  // Check if user is just the owner (propriétaire principal)
  const isOwner = (horse) => {
    if (!horse || !user) return false;
    return horse.proprietaire_id === user.id;
  };

  // Check if user is a PP (propriétaire partagé)
  const isPP = (horse) => {
    if (!horse || !user) return false;
    return horse.proprietaires_partages?.includes(user.id) && horse.proprietaire_id !== user.id;
  };

  // Check if user is a DP (demi-pensionnaire)
  const isDP = (horse) => {
    if (!horse || !user) return false;
    return horse.demi_pensionnaires?.includes(user.id) && !isOwnerOrPP(horse);
  };

  // Check if user can edit horse details (info, alimentation) - Owner/PP/DP can edit
  const canEditHorseDetails = (horse) => {
    if (!horse || !user) return false;
    if (isInstructor()) return true;
    // Owners, PP and DP can edit general info and alimentation
    return isOwnerOrPPOrDP(horse);
  };

  // Check if user can edit health/medical section - Owner/PP/DP can edit (same as details)
  const canEditHealthSection = (horse) => {
    if (!horse || !user) return false;
    if (isInstructor()) return true;
    // Owners, PP and DP can edit health section
    return isOwnerOrPPOrDP(horse);
  };

  // Check if user can manage photos - all (owner, PP, DP) can
  const canManagePhotos = (horse) => {
    if (!horse || !user) return false;
    if (isInstructor()) return true;
    return isOwnerOrPPOrDP(horse);
  };
  
  // Fetch medias for horse from gallery (+ migration auto de l'ancien système)
  const fetchHorseMedias = async (horseId, horse = null) => {
    if (!horseId) return;
    setHorseGalleryLoading(true);
    try {
      // Migration auto : si horse.photos[] existe, on migre vers media table (silencieux)
      const src = horse || selectedHorse;
      if (src?.photos?.length) {
        axios.post(`${API_URL}/api/gallery/migrate-horse-photos/${horseId}`)
          .catch(() => {});
      }
      const res = await axios.get(`${API_URL}/api/gallery/by-horse/${horseId}`);
      const sorted = (res.data || []).sort((a, b) =>
        new Date(b.created_at || b.upload_date) - new Date(a.created_at || a.upload_date)
      );
      setHorseMedias(sorted);
    } catch (error) {
      console.error('Error fetching horse medias:', error);
      setHorseMedias([]);
    } finally {
      setHorseGalleryLoading(false);
    }
  };
  
  // Load horse medias when horse is selected
  useEffect(() => {
    if (selectedHorse?.id) {
      fetchHorseMedias(selectedHorse.id, selectedHorse);
    } else {
      setHorseMedias([]);
    }
    // Reset viewer and modal states when horse changes
    setHorseViewerIndex(null);
    setHorseEditModalOpen(false);
    setMediaToEdit(null);
    setHorseSettingsModalOpen(false);
    setMediaToAdjust(null);
  }, [selectedHorse?.id]);

  // Check if user can add comments - instructors, owners, PP and DP
  const canAddComment = (horse) => {
    if (!horse || !user) return false;
    if (isInstructor()) return true;
    // Owners, PP and DP can add comments
    return isOwnerOrPPOrDP(horse);
  };

  // Check if user can manage cavaliers (PP/DP list) - ONLY instructors and owners (NOT PP, NOT DP)
  const canManageCavaliers = (horse) => {
    if (!horse || !user) return false;
    if (isInstructor()) return true;
    // Only owner can manage PP list (not DP list though)
    return isOwner(horse);
  };

  // Check if user can delete horse - ONLY instructors
  const canDeleteHorse = (horse) => {
    if (!horse || !user) return false;
    // Only instructors can delete - owners/PP/DP cannot
    return isInstructor();
  };

  // Check if user is owner or DP (for limited edit mode) - DEPRECATED, use specific functions
  const isOwnerOrDP = (horse) => {
    if (!horse || !user) return false;
    return horse.proprietaire_id === user.id || horse.demi_pensionnaires?.includes(user.id);
  };
  
  const [newHorse, setNewHorse] = useState({
    nom: '',
    type_animal: 'cheval',
    race: '',
    robe: '',
    annee_naissance: new Date().getFullYear() - 10,
    is_club_horse: false,
    type_pension: '',
    description: '',
    notes_sante: '',
    niveau_performance: ''
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);


  // Fetch horse riders when a horse is selected
  useEffect(() => {
    if (selectedHorse?.id) {
      fetchHorseRiders(selectedHorse.id);
    }
  }, [selectedHorse?.id]);

  // Handle URL parameters to open horse modal with specific filter
  useEffect(() => {
    const openHorseId = searchParams.get('openHorse');
    const filterParam = searchParams.get('commentFilter');
    
    if (openHorseId && horses.length > 0) {
      const horseToOpen = horses.find(h => h.id === openHorseId);
      if (horseToOpen) {
        setSelectedHorse(horseToOpen);
        setCollapsedCards(prev => ({ ...prev, medical: false }));
        if (filterParam && ['attention', 'a_surveiller', 'note', 'all'].includes(filterParam)) {
          setCommentFilter(filterParam);
        }
        // Clear URL params after opening
        setSearchParams({});
      }
    }
  }, [horses, searchParams, setSearchParams]);

  const fetchData = async () => {
    try {
      let horsesData = [];
      let usersData = [];
      
      if (isInstructor()) {
        // Instructors see all horses
        const params = statusFilter && statusFilter !== 'all' ? { statut: statusFilter } : {};
        const [horsesRes, usersRes] = await Promise.all([
          axios.get(`${API_URL}/api/horses`, { params }),
          axios.get(`${API_URL}/api/users`).catch(() => ({ data: [] }))
        ]);
        horsesData = horsesRes.data;
        usersData = usersRes.data.filter(u => 
          ['cavalier_proprio', 'cavalier_proprietaire', 'cavalier_pp', 'cavalier_dp', 'cavalier_forfait'].includes(u.role_club) && u.statut === 'actif'
        );
      } else {
        // Cavaliers only see their own horses
        const horsesRes = await axios.get(`${API_URL}/api/horses/my-horses`);
        horsesData = horsesRes.data;
      }
      
      // Force new array reference to trigger re-render
      setHorses([...horsesData]);
      setCavaliers(usersData);
      
      // Update selectedHorse if it's currently open (to sync photos, etc.)
      if (selectedHorse) {
        const updatedHorse = horsesData.find(h => h.id === selectedHorse.id);
        if (updatedHorse) {
          // Force new object reference to trigger re-render
          setSelectedHorse({...updatedHorse});
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHorse = async () => {
    if (!newHorse.nom) {
      toast.error('Le nom est obligatoire');
      return;
    }
    
    try {
      // Clean up empty string values for enum fields to prevent Pydantic validation errors
      const horseData = { ...newHorse };
      if (horseData.type_pension === '') delete horseData.type_pension;
      if (horseData.niveau_performance === '') delete horseData.niveau_performance;
      
      await axios.post(`${API_URL}/api/horses`, horseData);
      toast.success('Cheval ajouté avec succès');
      setIsDialogOpen(false);
      setNewHorse({
        nom: '',
        type_animal: 'cheval',
        race: '',
        robe: '',
        annee_naissance: new Date().getFullYear() - 10,
        is_club_horse: false,
        type_pension: '',
        description: '',
        notes_sante: '',
        niveau_performance: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating horse:', error.response?.data || error);
      toast.error('Erreur lors de l\'ajout du cheval');
    }
  };

  const handleUpdateHorse = async () => {
    try {
      // All users (instructors, owners, PP, DP) use PUT endpoint
      // Backend handles field restrictions based on user role
      const horseData = { ...selectedHorse };
      
      // Convert empty string to null for type_pension (enum validation)
      if (horseData.type_pension === '' || horseData.type_pension === undefined) {
        horseData.type_pension = null;
      }
      
      // If it's explicitly a club horse, ensure pension-related fields are null
      // But don't force is_club_horse=true just because proprietaire_id is null (could be "Inconnu")
      if (horseData.is_club_horse) {
        horseData.type_pension = null;
        horseData.tarif_pension_mensuel = null;
      }
      
      await axios.put(`${API_URL}/api/horses/${selectedHorse.id}`, horseData);
      toast.success('Cheval mis à jour');
      setEditingSection(null);
      fetchData();
      // Reload horse riders data to reflect changes in the Cavaliers tab
      fetchHorseRiders(selectedHorse.id);
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de la mise à jour');
      toast.error(message);
    }
  };

  // Helper pour obtenir l'URL d'une photo avec cache-busting si modifiée
  // (anciens handlers photos[] supprimés — utiliser le viewer media)

  const handleAssignHorse = async (proprietaireId, dpIds) => {
    try {
      await axios.put(`${API_URL}/api/horses/${selectedHorse.id}/assign`, {
        proprietaire_id: proprietaireId || null,
        demi_pensionnaires: dpIds || []
      });
      toast.success('Attribution mise à jour');
      setAssignMode(false);
      fetchHorseRiders(selectedHorse.id);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de l\'attribution'));
    }
  };

  const fetchHorseRiders = async (horseId) => {
    try {
      const res = await axios.get(`${API_URL}/api/horses/${horseId}/riders`);
      setHorseRiders(res.data);
    } catch (error) {
      console.error('Error fetching horse riders:', error);
    }
  };

  // ========== COMMENTAIRES ==========
  const handleAddComment = async () => {
    if (!selectedHorse || !newComment.content.trim()) {
      toast.error('Le contenu du commentaire est requis');
      return;
    }
    
    setAddingComment(true);
    try {
      const res = await axios.post(`${API_URL}/api/horses/${selectedHorse.id}/commentaires`, {
        type: newComment.type,
        content: newComment.content.trim()
      });
      
      // Update local state
      const updatedCommentaires = [...(selectedHorse.commentaires || []), res.data.commentaire];
      setSelectedHorse({ ...selectedHorse, commentaires: updatedCommentaires });
      setNewComment({ type: 'note', content: '' });
      toast.success('Commentaire ajouté');
      fetchData(); // Refresh to update markers in list
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de l\'ajout du commentaire'));
    } finally {
      setAddingComment(false);
    }
  };

  const handleValidateComment = async (keepMarker) => {
    if (!validationModal) return;
    
    const { commentaire, horse } = validationModal;
    
    try {
      await axios.put(`${API_URL}/api/horses/${horse.id}/commentaires/${commentaire.id}/validate`, {
        keep_marker: keepMarker
      });
      
      // Update local state
      const updatedCommentaires = (horse.commentaires || []).map(c => {
        if (c.id === commentaire.id) {
          return {
            ...c,
            validated_by: user.id,
            validated_by_name: `${user.prenom || ''} ${user.nom || ''}`.trim(),
            validated_at: new Date().toISOString(),
            is_marker_active: keepMarker
          };
        }
        return c;
      });
      
      if (selectedHorse?.id === horse.id) {
        setSelectedHorse({ ...selectedHorse, commentaires: updatedCommentaires });
      }
      
      setValidationModal(null);
      toast.success(keepMarker ? 'Commentaire validé, marqueur maintenu' : 'Commentaire validé, marqueur désactivé');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la validation'));
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedHorse || !window.confirm('Supprimer ce commentaire ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/horses/${selectedHorse.id}/commentaires/${commentId}`);
      
      const updatedCommentaires = (selectedHorse.commentaires || []).filter(c => c.id !== commentId);
      setSelectedHorse({ ...selectedHorse, commentaires: updatedCommentaires });
      toast.success('Commentaire supprimé');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  // Get active markers for a horse
  const getActiveMarkers = (horse) => {
    if (!horse?.commentaires) return [];
    return horse.commentaires.filter(c => 
      (c.type === 'attention' || c.type === 'a_surveiller') && c.is_marker_active
    );
  };

  // Count comments by type for a horse
  const getCommentCounts = (horse) => {
    if (!horse?.commentaires) return { attention: 0, a_surveiller: 0, note: 0 };
    return {
      attention: horse.commentaires.filter(c => c.type === 'attention').length,
      a_surveiller: horse.commentaires.filter(c => c.type === 'a_surveiller').length,
      note: horse.commentaires.filter(c => c.type === 'note').length
    };
  };

  const handleDeleteHorse = async () => {
    if (!horseToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/horses/${horseToDelete.id}`);
      toast.success(`${horseToDelete.nom} déplacé vers la corbeille`);
      setDeleteConfirmOpen(false);
      setHorseToDelete(null);
      setSelectedHorse(null);
      setIsDialogOpen(false);
      // Force refresh data after deletion
      await fetchData();
      // Also refresh deleted horses list
      await fetchDeletedHorses();
    } catch (error) {
      console.error('Error deleting horse:', error);
      toast.error(getErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  const fetchDeletedHorses = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/horses/trash`);
      setDeletedHorses(res.data);
    } catch (error) {
      console.error('Error fetching deleted horses:', error);
    }
  };

  const handleRestoreHorse = async (horseId, horseName) => {
    try {
      await axios.post(`${API_URL}/api/horses/${horseId}/restore`);
      toast.success(`${horseName} a été restauré`);
      await fetchDeletedHorses();
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la restauration'));
    }
  };

  const handlePermanentDelete = async (horseId, horseName) => {
    try {
      await axios.delete(`${API_URL}/api/horses/${horseId}?permanent=true`);
      toast.success(`${horseName} supprimé définitivement`);
      await fetchDeletedHorses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la suppression définitive'));
    }
  };

  const getAge = (annee) => {
    if (!annee) return '-';
    return new Date().getFullYear() - annee;
  };

  const getPensionTarif = (type) => {
    const pension = pensionTypes.find(p => p.value === type);
    return pension?.tarif || 0;
  };

  // Helper pour rendre l'en-tête de carte pliable avec bouton crayon
  // canEdit: détermine si le bouton d'édition doit être affiché
  const renderCollapsibleCardHeader = (title, Icon, section, bgColor, textColor, borderColor, canEdit = true) => (
    <div 
      className={`${bgColor} px-4 py-2 border-b ${borderColor} flex items-center justify-between cursor-pointer hover:brightness-95 transition-all`}
      onClick={() => toggleCard(section)}
    >
      <h3 className={`font-semibold ${textColor} flex items-center gap-2`}>
        <Icon className="w-4 h-4" />
        {title}
        {collapsedCards[section] ? (
          <ChevronDown className="w-4 h-4 ml-1 opacity-60" />
        ) : (
          <ChevronUp className="w-4 h-4 ml-1 opacity-60" />
        )}
      </h3>
      <div className="flex items-center gap-1">
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            className={`h-7 w-7 p-0 ${textColor} hover:bg-white/50`}
            onClick={(e) => {
              e.stopPropagation();
              setEditingSection(editingSection === section ? null : section);
              // Déplier la carte si on clique sur édition
              if (collapsedCards[section]) {
                setCollapsedCards(prev => ({ ...prev, [section]: false }));
              }
            }}
            data-testid={`edit-${section}-btn`}
          >
            {editingSection === section ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
          </Button>
        )}
      </div>
    </div>
  );

  // Version non-pliable pour compatibilité (photos qui a un comportement spécial)
  const renderCardHeader = (title, Icon, section, bgColor, textColor, borderColor, canEdit = true) => (
    <div className={`${bgColor} px-4 py-2 border-b ${borderColor} flex items-center justify-between`}>
      <h3 className={`font-semibold ${textColor} flex items-center gap-2`}>
        <Icon className="w-4 h-4" />
        {title}
      </h3>
      {canEdit && (
        <Button
          variant="ghost"
          size="sm"
          className={`h-7 w-7 p-0 ${textColor} hover:bg-white/50`}
          onClick={(e) => {
            e.stopPropagation();
            setEditingSection(editingSection === section ? null : section);
          }}
          data-testid={`edit-${section}-btn`}
        >
          {editingSection === section ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
        </Button>
      )}
    </div>
  );

  const filteredHorses = horses.filter(horse =>
    horse.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    horse.race?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fonction de rendu pour la carte de cheval
  const renderHorseCard = (horse) => {
    const status = horseStatuses.find(s => s.value === horse.statut) || horseStatuses[0];
    const niveau = niveauPerformanceOptions.find(n => n.value === horse.niveau_performance);
    const activeMarkers = getActiveMarkers(horse);
    
    // Compter les marqueurs par type
    const attentionCount = activeMarkers.filter(m => m.type === 'attention').length;
    const surveillerCount = activeMarkers.filter(m => m.type === 'a_surveiller').length;
    
    // Trouver le propriétaire si existe
    const proprietaire = horse.proprietaire_id 
      ? (cavaliers.find(c => c.id === horse.proprietaire_id) || 
         (horse.owner_info ? { id: horse.owner_info.id, prenom: horse.owner_info.prenom, nom: horse.owner_info.nom } : null))
      : null;
    
    const cardKey = horse.id;
    
    return (
      <Card 
        key={cardKey}
        className="card-hover cursor-pointer overflow-hidden relative"
        onClick={() => {
          setSelectedHorse(horse);
          setEditingSection(null);
        }}
        data-testid={`horse-card-${horse.id}`}
      >
        {/* Icônes de marqueurs actifs avec compteur */}
        {activeMarkers.length > 0 && (
          <div className="absolute top-2 right-2 z-10 flex gap-1">
            {attentionCount > 0 && (
              <div 
                className="relative w-7 h-7 rounded-full bg-red-500 flex items-center justify-center shadow-md cursor-pointer hover:scale-110 transition-transform"
                onClick={(e) => {
                  e.stopPropagation();
                  // Ouvrir la modale du cheval avec le filtre "attention" activé
                  setSelectedHorse(horse);
                  setCollapsedCards(prev => ({ ...prev, medical: false }));
                  setCommentFilter('attention');
                }}
                title={`${attentionCount} Attention !`}
              >
                <AlertTriangle className="w-4 h-4 text-white" />
                {attentionCount > 1 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-red-600 text-[10px] font-bold rounded-full flex items-center justify-center shadow border border-red-200">
                    {attentionCount}
                  </span>
                )}
              </div>
            )}
            {surveillerCount > 0 && (
              <div 
                className="relative w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center shadow-md cursor-pointer hover:scale-110 transition-transform"
                onClick={(e) => {
                  e.stopPropagation();
                  // Ouvrir la modale du cheval avec le filtre "a_surveiller" activé
                  setSelectedHorse(horse);
                  setCollapsedCards(prev => ({ ...prev, medical: false }));
                  setCommentFilter('a_surveiller');
                }}
                title={`${surveillerCount} À surveiller`}
              >
                <Eye className="w-4 h-4 text-white" />
                {surveillerCount > 1 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-orange-600 text-[10px] font-bold rounded-full flex items-center justify-center shadow border border-orange-200">
                    {surveillerCount}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        <CardContent className="p-0">
          <div className="flex min-h-[96px] sm:min-h-[120px]">
            {/* Photo section - full height */}
            <div className="w-24 sm:w-32 flex-shrink-0 bg-gradient-to-br from-amber-100 to-amber-200 relative self-stretch">
              {horse.photo_principale ? (
                <img
                  src={resolveUrl(horse.photo_principale)}
                  alt={horse.nom}
                  className="w-full h-full object-cover absolute inset-0"
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Cat className="w-10 h-10 text-amber-700" />
                </div>
              )}
            </div>
            
            {/* Info section - swipeable */}
            <div className="flex-1 p-4 min-w-0 overflow-x-auto scrollbar-hide">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <h3 className="font-semibold text-stone-800">{horse.nom}</h3>
                {niveau && (
                  <Badge className={niveau.color}>{niveau.label}</Badge>
                )}
              </div>
              
              <p className="text-sm text-stone-500 mt-1 whitespace-nowrap">
                {horse.type_animal === 'poney' ? 'Poney' : 'Cheval'}
                {horse.race && ` • ${horse.race}`}
                {horse.annee_naissance && ` • ${getAge(horse.annee_naissance)} ans`}
                {horse.sexe && ` • ${horse.sexe === 'male' ? 'Mâle' : horse.sexe === 'hongre' ? 'Hongre' : horse.sexe === 'jument' ? 'Jument' : horse.sexe === 'femelle' ? 'Jument' : horse.sexe}`}
              </p>
              
              {/* Propriétaire ou Cheval du club */}
              <p className="text-sm mt-1 whitespace-nowrap">
                {proprietaire ? (
                  <span className="text-blue-700 font-medium">
                    <User className="w-3 h-3 inline-block mr-1" />
                    Propriétaire : {proprietaire.prenom} {proprietaire.nom}
                  </span>
                ) : (
                  <span className="text-purple-700 font-medium">
                    <Building className="w-3 h-3 inline-block mr-1" />
                    Cheval du club
                  </span>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="horses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2E4A3D]">
            {isInstructor() ? 'Cavalerie' : (horses.length === 1 ? 'Mon cheval' : 'Mes chevaux')}
          </h1>
          <p className="text-stone-500 mt-1">
            {isInstructor() 
              ? `${horses.length} chevaux et poneys`
              : (horses.length === 0 
                  ? 'Aucun cheval attribué' 
                  : `${horses.length} cheval${horses.length > 1 ? 'x' : ''} attribué${horses.length > 1 ? 's' : ''}`
                )
            }
          </p>
        </div>
        
        {isInstructor() && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowTrash(true); fetchDeletedHorses(); }}
              className="text-stone-600"
              data-testid="trash-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Corbeille
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-action" data-testid="add-horse-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto mx-auto">
              <DialogHeader>
                <DialogTitle className="font-serif">Ajouter un cheval</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="form-label">Nom *</Label>
                  <Input
                    value={newHorse.nom}
                    onChange={(e) => setNewHorse({...newHorse, nom: e.target.value})}
                    placeholder="Ex: Spirit"
                    className="input-field"
                    data-testid="horse-name"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Type</Label>
                    <Select 
                      value={newHorse.type_animal} 
                      onValueChange={(v) => setNewHorse({...newHorse, type_animal: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cheval">Cheval</SelectItem>
                        <SelectItem value="poney">Poney</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="form-label">Année de naissance</Label>
                    <Input
                      type="number"
                      value={newHorse.annee_naissance}
                      onChange={(e) => setNewHorse({...newHorse, annee_naissance: parseInt(e.target.value)})}
                      className="input-field"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Race</Label>
                    <Input
                      value={newHorse.race}
                      onChange={(e) => setNewHorse({...newHorse, race: e.target.value})}
                      placeholder="Ex: Selle Français"
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <Label className="form-label">Robe</Label>
                    <Input
                      value={newHorse.robe}
                      onChange={(e) => setNewHorse({...newHorse, robe: e.target.value})}
                      placeholder="Ex: Bai"
                      className="input-field"
                    />
                  </div>
                </div>
                
                {/* Cheval du club switch */}
                <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-[#2E4A3D]" />
                    <div>
                      <Label className="font-medium">Cheval du club</Label>
                      <p className="text-xs text-stone-500">Les chevaux du club ne sont pas facturés en pension</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={newHorse.is_club_horse}
                    onCheckedChange={(checked) => setNewHorse({
                      ...newHorse, 
                      is_club_horse: checked,
                      type_pension: checked ? '' : newHorse.type_pension
                    })}
                    data-testid="est-club-switch"
                    className="h-5 w-5"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Type de pension - masqué pour les chevaux du club */}
                  {!newHorse.is_club_horse && (
                    <div>
                      <Label className="form-label">Type de pension</Label>
                      <Select 
                        value={newHorse.type_pension} 
                        onValueChange={(v) => setNewHorse({...newHorse, type_pension: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir une pension" />
                        </SelectTrigger>
                        <SelectContent>
                          {pensionTypes.map(pension => (
                            <SelectItem key={pension.value} value={pension.value}>
                              {pension.label} - {pension.tarif}€/mois
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className={newHorse.is_club_horse ? 'col-span-2' : ''}>
                    <Label className="form-label">Niveau de performance</Label>
                    <Select 
                      value={newHorse.niveau_performance} 
                      onValueChange={(v) => setNewHorse({...newHorse, niveau_performance: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un niveau" />
                      </SelectTrigger>
                      <SelectContent>
                        {niveauPerformanceOptions.map(niveau => (
                          <SelectItem key={niveau.value} value={niveau.value}>
                            {niveau.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label className="form-label">Description</Label>
                  <Textarea
                    value={newHorse.description}
                    onChange={(e) => setNewHorse({...newHorse, description: e.target.value})}
                    placeholder="Caractère, particularités..."
                    className="input-field min-h-[60px]"
                  />
                </div>
                
                <div>
                  <Label className="form-label">Notes santé / vétérinaires</Label>
                  <Textarea
                    value={newHorse.notes_sante}
                    onChange={(e) => setNewHorse({...newHorse, notes_sante: e.target.value})}
                    placeholder="Historique de santé, allergies..."
                    className="input-field min-h-[80px]"
                  />
                </div>
                
                <Button 
                  className="btn-primary w-full" 
                  onClick={handleCreateHorse}
                  data-testid="save-horse-btn"
                >
                  Enregistrer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}

        {/* Trash Dialog */}
        <Dialog open={showTrash} onOpenChange={setShowTrash}>
          <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto mx-auto">
            <DialogHeader>
              <DialogTitle className="font-serif flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Corbeille
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-3">
              {deletedHorses.length === 0 ? (
                <p className="text-center text-stone-500 py-8">La corbeille est vide</p>
              ) : (
                deletedHorses.map(horse => (
                  <div key={horse.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border">
                    <div>
                      <p className="font-medium text-[#2E4A3D]">{horse.nom}</p>
                      <p className="text-xs text-stone-500">
                        Supprimé le {horse.deleted_at ? new Date(horse.deleted_at).toLocaleDateString('fr-FR') : '-'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleRestoreHorse(horse.id, horse.nom)}
                        className="text-emerald-600 hover:bg-emerald-50"
                      >
                        Restaurer
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          if (window.confirm(`Supprimer définitivement ${horse.nom} ? Cette action est irréversible.`)) {
                            handlePermanentDelete(horse.id, horse.nom);
                          }
                        }}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      {/* Search and filters - only for instructors */}
      {isInstructor() && (
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              placeholder="Rechercher un cheval..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 input-field"
              data-testid="search-horses"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {horseStatuses.map(status => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Horses grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredHorses.length === 0 ? (
          <div className="col-span-full">
            <div className="empty-state">
              <Cat className="empty-state-icon" />
              <h3 className="empty-state-title">
                {isInstructor() ? 'Aucun cheval' : 'Aucun cheval attribué'}
              </h3>
              <p className="empty-state-description">
                {isInstructor() 
                  ? 'Ajoutez votre premier cheval pour commencer'
                  : 'Contactez l\'administration pour vous attribuer un cheval'
                }
              </p>
            </div>
          </div>
        ) : (
          filteredHorses.map(horse => renderHorseCard(horse))
        )}
      </div>

      {/* Horse detail modal - Mobile-first scrollable design */}
      {selectedHorse && (
        <Dialog open={!!selectedHorse} onOpenChange={(open) => { 
          // Don't close if image modals are open
          if (!open && horseViewerIndex === null && !horseEditModalOpen && !horseSettingsModalOpen) {
            setSelectedHorse(null); 
            setHorseRiders(null); 
            setAssignMode(false); 
            setEditingSection(null); 
            setCommentPanelType(null); 
          }
        }}>
          <DialogContent 
            className="w-[95vw] max-w-[95vw] sm:max-w-lg h-[90vh] max-h-[90vh] mx-auto p-0 flex flex-col"
            style={{ overflow: 'hidden' }}
            onPointerDownOutside={(e) => { 
              // Prevent close if any image modal is open
              if (horseViewerIndex !== null || horseEditModalOpen || horseSettingsModalOpen) {
                e.preventDefault(); 
              }
            }}
            onInteractOutside={(e) => { 
              // Prevent close if any image modal is open
              if (horseViewerIndex !== null || horseEditModalOpen || horseSettingsModalOpen) {
                e.preventDefault(); 
              }
            }}
            aria-describedby="horse-detail-description"
          >
            <DialogHeader className="sr-only">
              <DialogTitle>{selectedHorse.nom} - Détails du cheval</DialogTitle>
            </DialogHeader>
            <span id="horse-detail-description" className="sr-only">Fiche détaillée du cheval {selectedHorse.nom}</span>
            {/* Hero Header - fixed height for reliable scroll space, ~16:9 proportions */}
            <div className="relative w-full h-[160px] sm:h-[180px] flex-shrink-0 flex-grow-0">
              {selectedHorse.photo_principale ? (
                <img
                  src={addCacheBust(resolveUrl(selectedHorse.photo_principale), horseGridCacheBust)}
                  alt={selectedHorse.nom}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 9"><rect fill="%23f5f5f4" width="16" height="9"/></svg>';
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                  <Cat className="w-16 h-16 text-amber-600/50" />
                </div>
              )}
              {/* Gradient overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              {/* Horse name overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h2 className="text-2xl font-serif font-bold text-white drop-shadow-lg">{selectedHorse.nom}</h2>
                <p className="text-white/80 text-sm">
                  {selectedHorse.type_animal === 'poney' ? 'Poney' : 'Cheval'}
                  {selectedHorse.race && ` • ${selectedHorse.race}`}
                  {selectedHorse.annee_naissance && ` • ${getAge(selectedHorse.annee_naissance)} ans`}
                </p>
              </div>
              
              {/* Badges commentaires à droite, verticalement sous la croix de fermeture */}
              {(() => {
                const counts = getCommentCounts(selectedHorse);
                
                return (
                  <div className="absolute top-12 right-2 z-10 flex flex-col gap-2">
                    
                    {/* Badge Attention */}
                    <div 
                      className={`relative w-8 h-8 rounded-full flex items-center justify-center shadow-md cursor-pointer hover:scale-110 transition-all ${
                        counts.attention > 0 ? 'bg-red-500' : 'bg-stone-300 hover:bg-stone-400'
                      }`}
                      onClick={() => setCommentPanelType('attention')}
                      title={`${counts.attention} Attention !`}
                      data-testid="comment-badge-attention"
                    >
                      <AlertTriangle className={`w-4 h-4 ${counts.attention > 0 ? 'text-white' : 'text-stone-500'}`} />
                      <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full flex items-center justify-center shadow border ${
                        counts.attention > 0 
                          ? 'bg-white text-red-600 border-red-200' 
                          : 'bg-stone-200 text-stone-500 border-stone-300'
                      }`}>
                        {counts.attention}
                      </span>
                    </div>
                    
                    {/* Badge À surveiller */}
                    <div 
                      className={`relative w-8 h-8 rounded-full flex items-center justify-center shadow-md cursor-pointer hover:scale-110 transition-all ${
                        counts.a_surveiller > 0 ? 'bg-orange-500' : 'bg-stone-300 hover:bg-stone-400'
                      }`}
                      onClick={() => setCommentPanelType('a_surveiller')}
                      title={`${counts.a_surveiller} À surveiller`}
                      data-testid="comment-badge-surveiller"
                    >
                      <Eye className={`w-4 h-4 ${counts.a_surveiller > 0 ? 'text-white' : 'text-stone-500'}`} />
                      <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full flex items-center justify-center shadow border ${
                        counts.a_surveiller > 0 
                          ? 'bg-white text-orange-600 border-orange-200' 
                          : 'bg-stone-200 text-stone-500 border-stone-300'
                      }`}>
                        {counts.a_surveiller}
                      </span>
                    </div>
                    
                    {/* Badge Notes */}
                    <div 
                      className={`relative w-8 h-8 rounded-full flex items-center justify-center shadow-md cursor-pointer hover:scale-110 transition-all ${
                        counts.note > 0 ? 'bg-blue-500' : 'bg-stone-300 hover:bg-stone-400'
                      }`}
                      onClick={() => setCommentPanelType('note')}
                      title={`${counts.note} Notes`}
                      data-testid="comment-badge-note"
                    >
                      <StickyNote className={`w-4 h-4 ${counts.note > 0 ? 'text-white' : 'text-stone-500'}`} />
                      <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full flex items-center justify-center shadow border ${
                        counts.note > 0 
                          ? 'bg-white text-blue-600 border-blue-200' 
                          : 'bg-stone-200 text-stone-500 border-stone-300'
                      }`}>
                        {counts.note}
                      </span>
                    </div>
                  </div>
                );
              })()}
              
              {/* Close button */}
              <button 
                onClick={() => setSelectedHorse(null)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Action buttons - only delete button for instructors - hidden when comment panel is open */}
            {canDeleteHorse(selectedHorse) && !commentPanelType && (
              <div className="flex gap-2 px-4 py-3 border-b bg-stone-50 justify-end">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setHorseToDelete(selectedHorse);
                    setDeleteConfirmOpen(true);
                  }}
                  data-testid="delete-horse-btn"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Supprimer
                </Button>
              </div>
            )}

            {/* Info banner for DP users - hidden when comment panel is open */}
            {isDP(selectedHorse) && !commentPanelType && (
              <div className="mx-4 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  <span><strong>Accès Demi-Pensionnaire</strong> : Vous pouvez consulter les informations, gérer les photos et ajouter des commentaires sur ce cheval.</span>
                </p>
              </div>
            )}

            {/* Info banner for Owner/PP users - hidden when comment panel is open */}
            {isOwnerOrPP(selectedHorse) && !isInstructor() && !commentPanelType && (
              <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>
                    <strong>{isPP(selectedHorse) ? 'Accès Propriétaire Partagé' : 'Accès Propriétaire'}</strong> 
                  </span>
                </p>
              </div>
            )}

            {/* Panneau des commentaires en plein écran (overlay) */}
            {commentPanelType && (() => {
              const typeConfig = {
                attention: { bg: 'bg-red-50', border: 'border-red-200', title: 'Attention !', icon: AlertTriangle, iconColor: 'text-red-600', headerBg: 'bg-red-100', btnBg: 'bg-red-600 hover:bg-red-700', btnOutline: 'text-red-600 border-red-300 hover:bg-red-100' },
                a_surveiller: { bg: 'bg-orange-50', border: 'border-orange-200', title: 'À surveiller', icon: Eye, iconColor: 'text-orange-600', headerBg: 'bg-orange-100', btnBg: 'bg-orange-600 hover:bg-orange-700', btnOutline: 'text-orange-600 border-orange-300 hover:bg-orange-100' },
                note: { bg: 'bg-blue-50', border: 'border-blue-200', title: 'Notes', icon: StickyNote, iconColor: 'text-blue-600', headerBg: 'bg-blue-100', btnBg: 'bg-blue-600 hover:bg-blue-700', btnOutline: 'text-blue-600 border-blue-300 hover:bg-blue-100' }
              };
              const config = typeConfig[commentPanelType];
              const IconComp = config.icon;
              const filteredComments = (selectedHorse?.commentaires || []).filter(c => c.type === commentPanelType);

              return (
                <div className={`${config.bg} flex flex-col flex-1`}>
                  {/* Header fixe avec flèche retour et bouton Ajouter */}
                  <div className={`flex items-center gap-2 px-4 py-2 border-b ${config.border} ${config.headerBg} flex-shrink-0`}>
                    <button
                      onClick={() => setCommentPanelType(null)}
                      className="p-1 rounded-full hover:bg-white/50 transition-colors"
                      data-testid="comment-panel-back"
                    >
                      <ArrowLeft className="w-4 h-4 text-stone-700" />
                    </button>
                    <IconComp className={`w-4 h-4 ${config.iconColor}`} />
                    <h3 className={`font-semibold text-stone-800`}>{config.title}</h3>
                    <span className="text-sm text-stone-500">({filteredComments.length})</span>
                    
                    {/* Bouton Ajouter à droite */}
                    <div className="ml-auto flex items-center gap-2">
                      <ShareNotificationButton
                        contextType="cheval"
                        contextId={selectedHorse.id}
                        titre={`${selectedHorse.nom} - ${config.title}`}
                      />
                      {canAddComment(selectedHorse) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className={config.btnOutline}
                          onClick={() => setEditingSection(editingSection === 'add-comment-panel' ? null : 'add-comment-panel')}
                        >
                          {editingSection === 'add-comment-panel' ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                          {editingSection === 'add-comment-panel' ? 'Annuler' : 'Ajouter'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Formulaire d'ajout (affiché au clic sur Ajouter) */}
                  {editingSection === 'add-comment-panel' && canAddComment(selectedHorse) && (
                    <div className={`p-4 border-b ${config.border} flex-shrink-0`}>
                      <div className="bg-white rounded-lg p-3 border border-stone-200 space-y-3">
                        <Textarea
                          value={newComment.type === commentPanelType ? newComment.content : ''}
                          onChange={(e) => setNewComment({ type: commentPanelType, content: e.target.value })}
                          placeholder="Votre commentaire..."
                          className="bg-white min-h-[60px]"
                          rows={2}
                        />
                        <Button
                          size="sm"
                          className={`w-full ${config.btnBg} text-white`}
                          onClick={async () => {
                            if (!newComment.content.trim()) return;
                            setAddingComment(true);
                            try {
                              const res = await axios.post(`${API_URL}/api/horses/${selectedHorse.id}/commentaires`, {
                                type: commentPanelType,
                                content: newComment.content.trim()
                              });
                              const updatedCommentaires = [...(selectedHorse.commentaires || []), res.data.commentaire];
                              setSelectedHorse({ ...selectedHorse, commentaires: updatedCommentaires });
                              setNewComment({ type: 'note', content: '' });
                              setEditingSection(null);
                              toast.success('Commentaire ajouté');
                              fetchData(); // Refresh to update badges in list
                            } catch (error) {
                              toast.error('Erreur lors de l\'ajout');
                            } finally {
                              setAddingComment(false);
                            }
                          }}
                          disabled={addingComment || !newComment.content.trim()}
                        >
                          {addingComment ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                          Enregistrer
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Liste des commentaires scrollable */}
                  <div 
                    className="flex-1 px-4 py-2 space-y-3"
                    style={{ 
                      overflowY: 'auto', 
                      WebkitOverflowScrolling: 'touch',
                      minHeight: 0 
                    }}
                  >
                    {filteredComments.length > 0 ? (
                      filteredComments.slice().reverse().map(comment => {
                        // Instructeurs, créateur du commentaire, propriétaire ou PP peuvent supprimer
                        const canDeleteComment = isInstructor() || comment.created_by === user?.id || isOwnerOrPP(selectedHorse);
                        
                        return (
                          <div 
                            key={comment.id} 
                            className={`p-4 rounded-xl bg-white border ${config.border} shadow-sm`}
                          >
                            <p className="text-sm text-stone-800 leading-relaxed">{comment.content}</p>
                            <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between">
                              <div>
                                <p className="text-xs text-stone-500">
                                  Par <span className="font-medium">{comment.created_by_name || 'Inconnu'}</span>
                                </p>
                                <p className="text-xs text-stone-400">
                                  {comment.created_at ? format(new Date(comment.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr }) : ''}
                                </p>
                                {comment.validated_at && (
                                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                                    <Check className="w-3 h-3" />
                                    Validé par {comment.validated_by_name}
                                    {!comment.is_marker_active && ' (marqueur désactivé)'}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Bouton validation pour instructeurs */}
                                {isInstructor() && comment.is_marker_active && comment.type !== 'note' && !comment.validated_at && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                    onClick={() => setValidationModal({ commentaire: comment, horse: selectedHorse })}
                                  >
                                    <Check className="w-3 h-3 mr-1" />
                                    Valider
                                  </Button>
                                )}
                                {canDeleteComment && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-red-500 border-red-300 hover:bg-red-50"
                                    disabled={deletingCommentId === comment.id}
                                    onClick={async () => {
                                      if (deletingCommentId) return; // Prevent double-click
                                      setDeletingCommentId(comment.id);
                                      try {
                                        await axios.delete(`${API_URL}/api/horses/${selectedHorse.id}/commentaires/${comment.id}`);
                                        const updatedCommentaires = (selectedHorse.commentaires || []).filter(c => c.id !== comment.id);
                                        setSelectedHorse({ ...selectedHorse, commentaires: updatedCommentaires });
                                        toast.success('Commentaire supprimé');
                                        fetchData(); // Refresh badges in list
                                      } catch (error) {
                                        toast.error('Erreur lors de la suppression');
                                      } finally {
                                        setDeletingCommentId(null);
                                      }
                                    }}
                                  >
                                    {deletingCommentId === comment.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Trash2 className="w-3 h-3" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-center text-stone-500 py-8">Aucun commentaire de ce type</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Scrollable content with cards - hidden when comment panel is open */}
            {!commentPanelType && (
            <div 
              className="flex-1 p-3 sm:p-4 space-y-4" 
              style={{ 
                overflowY: 'auto', 
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                minHeight: 0 
              }}
            >
              
              {/* Card 1: Informations générales - Amber */}
              <div className="rounded-xl bg-amber-50 border border-amber-200 overflow-hidden">
                {renderCollapsibleCardHeader("Informations générales", Cat, "general", "bg-amber-100", "text-amber-900", "border-amber-200", canEditHorseDetails(selectedHorse))}
                {!collapsedCards.general && (
                <div className="p-3 sm:p-4">
                  {editingSection === 'general' && canEditHorseDetails(selectedHorse) ? (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-amber-800">Nom *</Label>
                        <Input
                          value={selectedHorse.nom}
                          onChange={(e) => setSelectedHorse({...selectedHorse, nom: e.target.value})}
                          className="bg-white"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-amber-800">Type</Label>
                          <Select 
                            value={selectedHorse.type_animal || 'cheval'} 
                            onValueChange={(v) => setSelectedHorse({...selectedHorse, type_animal: v})}
                          >
                            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cheval">Cheval</SelectItem>
                              <SelectItem value="poney">Poney</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-amber-800">Sexe</Label>
                          <Select 
                            value={selectedHorse.sexe || ''} 
                            onValueChange={(v) => setSelectedHorse({...selectedHorse, sexe: v})}
                          >
                            <SelectTrigger className="bg-white"><SelectValue placeholder="Choisir" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">Mâle</SelectItem>
                              <SelectItem value="hongre">Hongre</SelectItem>
                              <SelectItem value="jument">Jument</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-amber-800">Race</Label>
                          <Input
                            value={selectedHorse.race || ''}
                            onChange={(e) => setSelectedHorse({...selectedHorse, race: e.target.value})}
                            className="bg-white"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-amber-800">Robe</Label>
                          <Input
                            value={selectedHorse.robe || ''}
                            onChange={(e) => setSelectedHorse({...selectedHorse, robe: e.target.value})}
                            className="bg-white"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-amber-800">Année naissance</Label>
                          <Input
                            type="number"
                            value={selectedHorse.annee_naissance || ''}
                            onChange={(e) => setSelectedHorse({...selectedHorse, annee_naissance: parseInt(e.target.value)})}
                            className="bg-white"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-amber-800">Taille (cm)</Label>
                          <Input
                            value={selectedHorse.taille || ''}
                            onChange={(e) => setSelectedHorse({...selectedHorse, taille: e.target.value})}
                            className="bg-white"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-amber-800">Niveau performance</Label>
                        <Select 
                          value={selectedHorse.niveau_performance || ''} 
                          onValueChange={(v) => setSelectedHorse({...selectedHorse, niveau_performance: v})}
                        >
                          <SelectTrigger className="bg-white"><SelectValue placeholder="Choisir" /></SelectTrigger>
                          <SelectContent>
                            {niveauPerformanceOptions.map(n => (
                              <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-amber-800">Statut</Label>
                        <Select 
                          value={selectedHorse.statut || 'actif'} 
                          onValueChange={(v) => setSelectedHorse({...selectedHorse, statut: v})}
                        >
                          <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {horseStatuses.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-amber-700">Type:</span> <span className="font-medium">{selectedHorse.type_animal === 'poney' ? 'Poney' : 'Cheval'}</span></div>
                      <div><span className="text-amber-700">Sexe:</span> <span className="font-medium">{selectedHorse.sexe === 'male' ? 'Mâle' : selectedHorse.sexe === 'hongre' ? 'Hongre' : selectedHorse.sexe === 'jument' ? 'Jument' : selectedHorse.sexe === 'femelle' ? 'Jument' : selectedHorse.sexe || '-'}</span></div>
                      <div><span className="text-amber-700">Race:</span> <span className="font-medium">{selectedHorse.race || '-'}</span></div>
                      <div><span className="text-amber-700">Robe:</span> <span className="font-medium">{selectedHorse.robe || '-'}</span></div>
                      <div><span className="text-amber-700">Âge:</span> <span className="font-medium">{selectedHorse.annee_naissance ? `${getAge(selectedHorse.annee_naissance)} ans` : '-'}</span></div>
                      <div><span className="text-amber-700">Taille:</span> <span className="font-medium">{selectedHorse.taille ? `${selectedHorse.taille} cm` : '-'}</span></div>
                      <div className="col-span-2"><span className="text-amber-700">Niveau:</span> <span className="font-medium">{niveauPerformanceOptions.find(n => n.value === selectedHorse.niveau_performance)?.label || '-'}</span></div>
                      <div className="col-span-2 flex items-center gap-2">
                        <span className="text-amber-700">Statut:</span>
                        <Badge className={horseStatuses.find(s => s.value === selectedHorse.statut)?.color}>
                          {horseStatuses.find(s => s.value === selectedHorse.statut)?.label || 'Actif'}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Card 2: Galerie Médias - Style page galerie */}
              <div className="rounded-xl bg-stone-50 border border-stone-200 overflow-hidden">
                <div 
                  className="bg-stone-100 px-4 py-2 border-b border-stone-200 flex items-center justify-between cursor-pointer hover:brightness-95 transition-all"
                  onClick={() => toggleCard('photos')}
                >
                  <h3 className="font-semibold text-stone-800 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Galerie
                    <Badge variant="outline" className="text-[10px] ml-1 bg-stone-100 text-stone-600 border-stone-300">
                      {horseMedias.length}
                    </Badge>
                    {collapsedCards.photos ? (
                      <ChevronDown className="w-4 h-4 ml-1 opacity-60" />
                    ) : (
                      <ChevronUp className="w-4 h-4 ml-1 opacity-60" />
                    )}
                  </h3>
                  <div className="flex items-center gap-2">
                    {/* Zoom slider - only when expanded */}
                    {!collapsedCards.photos && horseMedias.length > 0 && (
                      <input
                        type="range"
                        min="2"
                        max="5"
                        value={horseGalleryZoom}
                        onChange={(e) => {
                          e.stopPropagation();
                          setHorseGalleryZoom(parseInt(e.target.value));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-12 h-1 appearance-none bg-stone-300 rounded-full cursor-pointer accent-[#2E4A3D]"
                      />
                    )}
                    {canManagePhotos(selectedHorse) && (
                      <div className="flex items-center gap-2">
                        {isInstructor() && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await axios.post(`${API_URL}/api/gallery/cleanup/orphans`);
                                toast.success(res.data.message);
                                if (res.data.deleted > 0) fetchHorseMedias(selectedHorse.id);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erreur nettoyage');
                              }
                            }}
                            className="w-7 h-7 rounded-full bg-stone-400 text-white flex items-center justify-center"
                            title="Nettoyer les médias orphelins"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setMediaUploadOpen(true);
                          }}
                          className="w-7 h-7 rounded-full bg-[#2E4A3D] text-white flex items-center justify-center text-sm"
                          data-testid="add-photo-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {!collapsedCards.photos && (
                  <div className="p-1">
                    {horseGalleryLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                      </div>
                    ) : horseMedias.length > 0 ? (
                      <div 
                        className="grid gap-0.5"
                        style={{ gridTemplateColumns: `repeat(${horseGalleryZoom}, minmax(0, 1fr))` }}
                      >
                        {horseMedias.map((media, index) => (
                          <HorseMediaThumb
                            key={media.id}
                            media={media}
                            index={index}
                            cacheBust={horseGridCacheBust}
                            onOpen={(i) => {
                              const m = horseMedias[i];
                              setHorseViewerRotation(0);
                              setHorseViewerCropMode(false);
                              setHorseViewerCrop(undefined);
                              setHorseViewerCompletedCrop(null);
                              setHorseViewerPendingCrop(null);
                              // Session bust (post-apply) > updated_at persistant > 0
                              setHorseViewerCacheBust(
                                horseGridCacheBust || (m?.updated_at ? new Date(m.updated_at).getTime() : 0)
                              );
                              setHorseViewerIndex(i);
                            }}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <ImageIcon className="w-10 h-10 text-stone-300 mx-auto mb-2" />
                        <p className="text-sm text-stone-500">Aucun média</p>
                        {canManagePhotos(selectedHorse) && (
                          <button
                            onClick={() => setMediaUploadOpen(true)}
                            className="mt-2 text-sm text-[#2E4A3D] font-medium"
                          >
                            + Ajouter des médias
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Fullscreen Viewer for Horse Gallery */}
              {horseViewerIndex !== null && horseMedias[horseViewerIndex] && createPortal(
                <div 
                  className="fixed inset-0 bg-black flex flex-col items-center justify-center"
                  style={{ zIndex: 99999 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Close button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setHorseViewerIndex(null);
                      setHorseViewerRotation(0);
                      setHorseViewerCropMode(false);
                      setHorseViewerCrop(undefined);
                      setHorseViewerCompletedCrop(null);
                      setHorseViewerPendingCrop(null);
                      setHorseViewerCacheBust(0);
                    }}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center z-10 pointer-events-auto"
                    style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                  
                  {/* Counter */}
                  <div 
                    className="absolute top-4 left-4 bg-black/50 px-3 py-1.5 rounded-full text-white text-sm z-10"
                    style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
                  >
                    {horseViewerIndex + 1} / {horseMedias.length}
                  </div>
                  
                  {/* Previous button */}
                  {horseViewerIndex > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setHorseViewerRotation(0);
                        setHorseViewerCropMode(false);
                        setHorseViewerCrop(undefined);
                        setHorseViewerCompletedCrop(null);
                        setHorseViewerPendingCrop(null);
                        setHorseViewerIndex(horseViewerIndex - 1);
                      }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center z-10 pointer-events-auto"
                    >
                      <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                  )}
                  
                  {/* Next button */}
                  {horseViewerIndex < horseMedias.length - 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setHorseViewerRotation(0);
                        setHorseViewerCropMode(false);
                        setHorseViewerCrop(undefined);
                        setHorseViewerCompletedCrop(null);
                        setHorseViewerPendingCrop(null);
                        setHorseViewerIndex(horseViewerIndex + 1);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center z-10 pointer-events-auto"
                    >
                      <ChevronRight className="w-6 h-6 text-white" />
                    </button>
                  )}
                  
                  {/* Media display */}
                  <div className="w-full h-full flex items-center justify-center p-2 pointer-events-none">
                    {horseMedias[horseViewerIndex].media_type === 'image' ? (
                      horseViewerCropMode ? (
                        <div className="pointer-events-auto max-w-full max-h-full">
                          <ReactCrop
                            crop={horseViewerCrop}
                            onChange={c => setHorseViewerCrop(c)}
                            onComplete={c => setHorseViewerCompletedCrop(c)}
                          >
                            <img
                              ref={horseViewerImgRef}
                              src={mediaViewerUrl(horseMedias[horseViewerIndex], horseViewerCacheBust)}
                              alt=""
                              style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain' }}
                            />
                          </ReactCrop>
                        </div>
                      ) : (
                        <img
                          src={addCacheBust(resolveUrl(horseMedias[horseViewerIndex].variants?.screen?.url || horseMedias[horseViewerIndex].file_url), horseViewerCacheBust)}
                          alt=""
                          className="max-w-full max-h-full object-contain pointer-events-auto"
                          style={{ transform: `rotate(${horseViewerRotation}deg)`, transition: 'transform 0.2s' }}
                        />
                      )
                    ) : (
                      <video
                        src={horseMedias[horseViewerIndex].file_url?.startsWith('http') ? horseMedias[horseViewerIndex].file_url : `${API_URL}${horseMedias[horseViewerIndex].file_url}`}
                        className="max-w-full max-h-full object-contain pointer-events-auto"
                        controls
                        autoPlay
                        playsInline
                      />
                    )}
                  </div>
                  
                  {/* Bottom zone = bandeau modifs + barre actions */}
                  <div
                    className="absolute bottom-0 left-0 right-0 pointer-events-auto"
                    style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Bandeau modifications en attente */}
                    {(horseViewerRotation !== 0 || horseViewerPendingCrop) && (
                      <div className="flex items-center justify-between bg-amber-500/90 px-4 py-2">
                        <span className="text-white text-xs font-medium flex items-center gap-2">
                          {horseViewerRotation !== 0 && <span className="flex items-center gap-1"><RotateCw className="w-3 h-3" />{horseViewerRotation}°</span>}
                          {horseViewerPendingCrop && <span className="flex items-center gap-1"><Crop className="w-3 h-3" />Recadrage</span>}
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setHorseViewerRotation(0); setHorseViewerPendingCrop(null); }}
                            disabled={horseViewerApplying}
                            className="flex items-center gap-1 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white text-xs font-medium disabled:opacity-40"
                          >
                            <X className="w-3 h-3" /> Annuler
                          </button>
                          <button
                            type="button"
                            disabled={horseViewerApplying}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const media = horseMedias[horseViewerIndex];
                              if (!media) return;
                              setHorseViewerApplying(true);
                              try {
                                if (horseViewerRotation !== 0) {
                                  await axios.post(`${API_URL}/api/gallery/${media.id}/rotate`, { angle: horseViewerRotation });
                                }
                                if (horseViewerPendingCrop) {
                                  await axios.post(`${API_URL}/api/gallery/${media.id}/crop`, horseViewerPendingCrop);
                                }
                                toast.success('Modifications appliquées');
                                setHorseViewerRotation(0);
                                setHorseViewerPendingCrop(null);
                                const ts = Date.now();
                                setHorseViewerCacheBust(ts);
                                setHorseGridCacheBust(ts);
                                await fetchHorseMedias(selectedHorse.id);
                              } catch {
                                toast.error("Erreur lors de l'application");
                              } finally {
                                setHorseViewerApplying(false);
                              }
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 bg-white text-amber-700 hover:bg-white/90 rounded-lg text-xs font-semibold disabled:opacity-40"
                          >
                            {horseViewerApplying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            Appliquer
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Barre d'actions principale */}
                    {horseViewerCropMode ? (
                      /* Mode recadrage : Annuler / Confirmer uniquement */
                      <div className="bg-black/90 px-4 py-3 flex justify-center gap-4">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setHorseViewerCropMode(false); setHorseViewerCrop(undefined); setHorseViewerCompletedCrop(null); }}
                          className="flex items-center gap-1.5 px-5 py-2.5 bg-white/20 hover:bg-white/30 rounded-xl text-white text-sm font-medium"
                        >
                          <X className="w-4 h-4" /> Annuler recadrage
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (horseViewerCompletedCrop && horseViewerImgRef.current &&
                                horseViewerCompletedCrop.width > 5 && horseViewerCompletedCrop.height > 5) {
                              const img = horseViewerImgRef.current;
                              const scaleX = img.naturalWidth / (img.width || 1);
                              const scaleY = img.naturalHeight / (img.height || 1);
                              const imgW = img.naturalWidth;
                              const imgH = img.naturalHeight;
                              const x = Math.max(0, Math.round(horseViewerCompletedCrop.x * scaleX));
                              const y = Math.max(0, Math.round(horseViewerCompletedCrop.y * scaleY));
                              const w = Math.min(imgW - x, Math.round(horseViewerCompletedCrop.width * scaleX));
                              const h = Math.min(imgH - y, Math.round(horseViewerCompletedCrop.height * scaleY));
                              if (w > 5 && h > 5) {
                                setHorseViewerPendingCrop({ x, y, width: w, height: h });
                              } else {
                                toast.error('Sélection trop petite');
                                return;
                              }
                            } else if (!horseViewerCompletedCrop || horseViewerCompletedCrop.width <= 5) {
                              toast.error('Dessinez une zone de recadrage');
                              return;
                            }
                            setHorseViewerCropMode(false);
                            setHorseViewerCrop(undefined);
                            setHorseViewerCompletedCrop(null);
                          }}
                          className="flex items-center gap-1.5 px-5 py-2.5 bg-green-500 hover:bg-green-600 rounded-xl text-white text-sm font-medium"
                        >
                          <Check className="w-4 h-4" /> Confirmer recadrage
                        </button>
                      </div>
                    ) : (
                      /* Boutons normaux — toujours visibles */
                      <div className="bg-black/80 px-4 py-3 flex justify-center gap-2">
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await axios.put(`${API_URL}/api/horses/${selectedHorse.id}/photo-principale`, {
                                photo_url: horseMedias[horseViewerIndex].file_url
                              });
                              setSelectedHorse({...selectedHorse, photo_principale: horseMedias[horseViewerIndex].file_url});
                              setHorses(horses.map(h => h.id === selectedHorse.id ? {...h, photo_principale: horseMedias[horseViewerIndex].file_url} : h));
                              toast.success('Photo principale mise à jour');
                            } catch {
                              toast.error('Erreur lors de la mise à jour');
                            }
                          }}
                          className="w-14 h-14 flex flex-col items-center justify-center text-amber-400 hover:bg-amber-500/20 rounded-lg transition-colors"
                        >
                          <Star className="w-5 h-5" />
                          <span className="text-[9px] mt-0.5">Préférée</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMediaToEdit(horseMedias[horseViewerIndex]);
                            setHorseEditModalOpen(true);
                            setHorseViewerIndex(null);
                          }}
                          className="w-14 h-14 flex flex-col items-center justify-center text-white hover:bg-white/20 rounded-lg transition-colors"
                          data-testid="horse-viewer-edit-btn"
                        >
                          <Edit2 className="w-5 h-5" />
                          <span className="text-[9px] mt-0.5">Infos</span>
                        </button>
                        {horseMedias[horseViewerIndex]?.media_type === 'image' && (<>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setHorseViewerRotation(r => (r - 90 + 360) % 360); }}
                            className="w-14 h-14 flex flex-col items-center justify-center text-white hover:bg-white/20 rounded-lg transition-colors"
                          >
                            <RotateCcw className="w-5 h-5" />
                            <span className="text-[9px] mt-0.5">Gauche</span>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setHorseViewerRotation(r => (r + 90) % 360); }}
                            className="w-14 h-14 flex flex-col items-center justify-center text-white hover:bg-white/20 rounded-lg transition-colors"
                          >
                            <RotateCw className="w-5 h-5" />
                            <span className="text-[9px] mt-0.5">Droite</span>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setHorseViewerCropMode(true); }}
                            className="w-14 h-14 flex flex-col items-center justify-center text-white hover:bg-white/20 rounded-lg transition-colors"
                          >
                            <Crop className="w-5 h-5" />
                            <span className="text-[9px] mt-0.5">Recadrer</span>
                          </button>
                        </>)}
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const media = horseMedias[horseViewerIndex];
                            if (!media?.id) { toast.error('ID média introuvable'); return; }
                            if (window.confirm('Supprimer ce média ?')) {
                              try {
                                await axios.delete(`${API_URL}/api/gallery/${media.id}`);
                                toast.success('Média supprimé');
                                setHorseViewerIndex(null);
                                setHorseViewerRotation(0);
                                setHorseViewerPendingCrop(null);
                                setHorseGridCacheBust(Date.now());
                                fetchHorseMedias(selectedHorse.id);
                              } catch (err) {
                                console.error('Delete error:', err.response?.data || err);
                                toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
                              }
                            }
                          }}
                          className="w-14 h-14 flex flex-col items-center justify-center text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                          data-testid="horse-viewer-delete-btn"
                        >
                          <Trash2 className="w-5 h-5" />
                          <span className="text-[9px] mt-0.5">Supprimer</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>,
                document.body
              )}


              {/* Card 3: Santé - Green */}
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 overflow-hidden">
                {renderCollapsibleCardHeader("Santé & Soins", Heart, "medical", "bg-emerald-100", "text-emerald-900", "border-emerald-200", canEditHealthSection(selectedHorse))}
                {!collapsedCards.medical && (
                <div className="p-4 space-y-3">
                  {editingSection === 'medical' && canEditHealthSection(selectedHorse) ? (
                    <div className="space-y-4">
                      {/* Maréchal */}
                      <div className="p-3 bg-white rounded-lg border border-emerald-200">
                        <Label className="text-xs text-emerald-800 font-medium">🔧 Maréchal</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <span className="text-xs text-gray-500">Dernier soin</span>
                            <Input
                              type="date"
                              value={selectedHorse.soins_marechal?.derniere_visite?.split('T')[0] || ''}
                              onChange={(e) => setSelectedHorse({
                                ...selectedHorse, 
                                soins_marechal: {...(selectedHorse.soins_marechal || {}), derniere_visite: e.target.value}
                              })}
                              className="mt-1 bg-white"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">Fréquence</span>
                            <Select 
                              value={String(selectedHorse.soins_marechal?.frequence_semaines ?? 6)}
                              onValueChange={(v) => setSelectedHorse({
                                ...selectedHorse, 
                                soins_marechal: {...(selectedHorse.soins_marechal || {}), frequence_semaines: parseInt(v)}
                              })}
                            >
                              <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {frequencesSemaines.map(f => (
                                  <SelectItem key={f.value} value={String(f.value)}>{f.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      {/* Dentiste */}
                      <div className="p-3 bg-white rounded-lg border border-emerald-200">
                        <Label className="text-xs text-emerald-800 font-medium">🦷 Dentiste</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <span className="text-xs text-gray-500">Dernier soin</span>
                            <Input
                              type="date"
                              value={selectedHorse.soins_dentiste?.derniere_visite?.split('T')[0] || ''}
                              onChange={(e) => setSelectedHorse({
                                ...selectedHorse, 
                                soins_dentiste: {...(selectedHorse.soins_dentiste || {}), derniere_visite: e.target.value}
                              })}
                              className="mt-1 bg-white"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">Fréquence</span>
                            <Select 
                              value={String(selectedHorse.soins_dentiste?.frequence_mois ?? 12)}
                              onValueChange={(v) => setSelectedHorse({
                                ...selectedHorse, 
                                soins_dentiste: {...(selectedHorse.soins_dentiste || {}), frequence_mois: parseInt(v)}
                              })}
                            >
                              <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {frequencesMois.map(f => (
                                  <SelectItem key={f.value} value={String(f.value)}>{f.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      {/* Vétérinaire/Vaccin */}
                      <div className="p-3 bg-white rounded-lg border border-emerald-200">
                        <Label className="text-xs text-emerald-800 font-medium">🩺 Vétérinaire/Vaccin</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <span className="text-xs text-gray-500">Dernier soin</span>
                            <Input
                              type="date"
                              value={selectedHorse.soins_veterinaire?.derniere_visite?.split('T')[0] || ''}
                              onChange={(e) => setSelectedHorse({
                                ...selectedHorse, 
                                soins_veterinaire: {...(selectedHorse.soins_veterinaire || {}), derniere_visite: e.target.value}
                              })}
                              className="mt-1 bg-white"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">Fréquence</span>
                            <Select 
                              value={String(selectedHorse.soins_veterinaire?.frequence_mois ?? 12)}
                              onValueChange={(v) => setSelectedHorse({
                                ...selectedHorse, 
                                soins_veterinaire: {...(selectedHorse.soins_veterinaire || {}), frequence_mois: parseInt(v)}
                              })}
                            >
                              <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {frequencesMois.map(f => (
                                  <SelectItem key={f.value} value={String(f.value)}>{f.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      {/* Vermifuge */}
                      <div className="p-3 bg-white rounded-lg border border-emerald-200">
                        <Label className="text-xs text-emerald-800 font-medium">💊 Vermifuge</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <span className="text-xs text-gray-500">Dernier soin</span>
                            <Input
                              type="date"
                              value={selectedHorse.soins_vermifuge?.derniere_visite?.split('T')[0] || ''}
                              onChange={(e) => setSelectedHorse({
                                ...selectedHorse, 
                                soins_vermifuge: {...(selectedHorse.soins_vermifuge || {}), derniere_visite: e.target.value}
                              })}
                              className="mt-1 bg-white"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">Fréquence</span>
                            <Select 
                              value={String(selectedHorse.soins_vermifuge?.frequence_mois ?? 6)}
                              onValueChange={(v) => setSelectedHorse({
                                ...selectedHorse, 
                                soins_vermifuge: {...(selectedHorse.soins_vermifuge || {}), frequence_mois: parseInt(v)}
                              })}
                            >
                              <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {frequencesMois.map(f => (
                                  <SelectItem key={f.value} value={String(f.value)}>{f.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="mt-2">
                          <span className="text-xs text-gray-500">Produit utilisé</span>
                          <Input
                            value={selectedHorse.soins_vermifuge?.produit || ''}
                            onChange={(e) => setSelectedHorse({
                              ...selectedHorse, 
                              soins_vermifuge: {...(selectedHorse.soins_vermifuge || {}), produit: e.target.value}
                            })}
                            placeholder="Nom du produit..."
                            className="mt-1 bg-white"
                          />
                        </div>
                      </div>
                      {/* Ostéopathe */}
                      <div className="p-3 bg-white rounded-lg border border-emerald-200">
                        <Label className="text-xs text-emerald-800 font-medium">🦴 Ostéopathe</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <span className="text-xs text-gray-500">Dernier soin</span>
                            <Input
                              type="date"
                              value={selectedHorse.soins_osteopathe?.derniere_visite?.split('T')[0] || ''}
                              onChange={(e) => setSelectedHorse({
                                ...selectedHorse, 
                                soins_osteopathe: {...(selectedHorse.soins_osteopathe || {}), derniere_visite: e.target.value}
                              })}
                              className="mt-1 bg-white"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">Fréquence</span>
                            <Select 
                              value={String(selectedHorse.soins_osteopathe?.frequence_mois ?? 6)}
                              onValueChange={(v) => setSelectedHorse({
                                ...selectedHorse, 
                                soins_osteopathe: {...(selectedHorse.soins_osteopathe || {}), frequence_mois: parseInt(v)}
                              })}
                            >
                              <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {frequencesMois.map(f => (
                                  <SelectItem key={f.value} value={String(f.value)}>{f.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      {/* Notes santé */}
                      <div>
                        <Label className="text-xs text-emerald-800 font-medium">📝 Notes santé</Label>
                        <Textarea
                          value={selectedHorse.notes_sante || ''}
                          onChange={(e) => setSelectedHorse({...selectedHorse, notes_sante: e.target.value})}
                          placeholder="Notes sur la santé du cheval..."
                          className="mt-1 bg-white"
                          rows={3}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Maréchal - avec calcul automatique */}
                      {(() => {
                        const derniere = selectedHorse.soins_marechal?.derniere_visite;
                        const freq = selectedHorse.soins_marechal?.frequence_semaines ?? 6;
                        const nextDate = calculateNextDate(derniere, freq, null);
                        const status = getCareStatus(nextDate);
                        return (
                          <div className="flex items-center justify-between py-2 border-b border-emerald-100">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center text-sm">🔧</div>
                              <div>
                                <span className="text-sm font-medium text-emerald-900">Maréchal</span>
                                <div className="text-xs text-gray-500">
                                  {freq > 0 ? `Tous les ${freq} sem.` : 'Fréquence non définie'}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {derniere && <div className="text-xs text-gray-500">Dernier: {new Date(derniere).toLocaleDateString('fr-FR')}</div>}
                              <div className={`text-sm ${status.color}`}>
                                {nextDate ? `Prochain: ${nextDate.toLocaleDateString('fr-FR')}` : 'Non planifié'}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Dentiste */}
                      {(() => {
                        const derniere = selectedHorse.soins_dentiste?.derniere_visite;
                        const freq = selectedHorse.soins_dentiste?.frequence_mois ?? 12;
                        const nextDate = calculateNextDate(derniere, null, freq);
                        const status = getCareStatus(nextDate);
                        return (
                          <div className="flex items-center justify-between py-2 border-b border-emerald-100">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center text-sm">🦷</div>
                              <div>
                                <span className="text-sm font-medium text-emerald-900">Dentiste</span>
                                <div className="text-xs text-gray-500">
                                  {freq > 0 ? `Tous les ${freq} mois` : 'Fréquence non définie'}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {derniere && <div className="text-xs text-gray-500">Dernier: {new Date(derniere).toLocaleDateString('fr-FR')}</div>}
                              <div className={`text-sm ${status.color}`}>
                                {nextDate ? `Prochain: ${nextDate.toLocaleDateString('fr-FR')}` : 'Non planifié'}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Vétérinaire */}
                      {(() => {
                        const derniere = selectedHorse.soins_veterinaire?.derniere_visite;
                        const freq = selectedHorse.soins_veterinaire?.frequence_mois ?? 12;
                        const nextDate = calculateNextDate(derniere, null, freq);
                        const status = getCareStatus(nextDate);
                        return (
                          <div className="flex items-center justify-between py-2 border-b border-emerald-100">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center text-sm">🩺</div>
                              <div>
                                <span className="text-sm font-medium text-emerald-900">Vétérinaire</span>
                                <div className="text-xs text-gray-500">
                                  {freq > 0 ? `Tous les ${freq} mois` : 'Fréquence non définie'}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {derniere && <div className="text-xs text-gray-500">Dernier: {new Date(derniere).toLocaleDateString('fr-FR')}</div>}
                              <div className={`text-sm ${status.color}`}>
                                {nextDate ? `Prochain: ${nextDate.toLocaleDateString('fr-FR')}` : 'Non planifié'}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Vermifuge */}
                      {(() => {
                        const derniere = selectedHorse.soins_vermifuge?.derniere_visite;
                        const freq = selectedHorse.soins_vermifuge?.frequence_mois ?? 6;
                        const nextDate = calculateNextDate(derniere, null, freq);
                        const status = getCareStatus(nextDate);
                        return (
                          <div className="flex items-center justify-between py-2 border-b border-emerald-100">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center text-sm">💊</div>
                              <div>
                                <span className="text-sm font-medium text-emerald-900">Vermifuge</span>
                                <div className="text-xs text-gray-500">
                                  {freq > 0 ? `Tous les ${freq} mois` : 'Fréquence non définie'}
                                  {selectedHorse.soins_vermifuge?.produit && ` • ${selectedHorse.soins_vermifuge.produit}`}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {derniere && <div className="text-xs text-gray-500">Dernier: {new Date(derniere).toLocaleDateString('fr-FR')}</div>}
                              <div className={`text-sm ${status.color}`}>
                                {nextDate ? `Prochain: ${nextDate.toLocaleDateString('fr-FR')}` : 'Non planifié'}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Ostéopathe */}
                      {(() => {
                        const derniere = selectedHorse.soins_osteopathe?.derniere_visite;
                        const freq = selectedHorse.soins_osteopathe?.frequence_mois ?? 6;
                        const nextDate = calculateNextDate(derniere, null, freq);
                        const status = getCareStatus(nextDate);
                        return (
                          <div className="flex items-center justify-between py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center text-sm">🦴</div>
                              <div>
                                <span className="text-sm font-medium text-emerald-900">Ostéopathe</span>
                                <div className="text-xs text-gray-500">
                                  {freq > 0 ? `Tous les ${freq} mois` : 'Fréquence non définie'}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {derniere && <div className="text-xs text-gray-500">Dernier: {new Date(derniere).toLocaleDateString('fr-FR')}</div>}
                              <div className={`text-sm ${status.color}`}>
                                {nextDate ? `Prochain: ${nextDate.toLocaleDateString('fr-FR')}` : 'Non planifié'}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Notes santé */}
                      {selectedHorse.notes_sante && (
                        <div className="mt-3 p-3 bg-emerald-100 rounded-lg">
                          <p className="text-xs text-emerald-800 font-medium mb-1">Notes santé:</p>
                          <p className="text-sm text-emerald-900">{selectedHorse.notes_sante}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}
              </div>
              {/* Card 4: Alimentation - Orange */}
              <div className="rounded-xl bg-orange-50 border border-orange-200 overflow-hidden">
                {renderCollapsibleCardHeader("Alimentation", Leaf, "alimentation", "bg-orange-100", "text-orange-900", "border-orange-200", canEditHorseDetails(selectedHorse))}
                {!collapsedCards.alimentation && (
                <div className="p-4 space-y-3 text-sm">
                  {editingSection === 'alimentation' && canEditHorseDetails(selectedHorse) ? (
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs text-orange-800 font-medium">🥕 Détail des rations</Label>
                        <Textarea
                          value={selectedHorse.detail_rations || ''}
                          onChange={(e) => setSelectedHorse({...selectedHorse, detail_rations: e.target.value})}
                          placeholder="Ex: 2L granulés matin, 1L soir, foin à volonté..."
                          className="mt-1 bg-white"
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-orange-800 font-medium">⚠️ Précautions</Label>
                        <Textarea
                          value={selectedHorse.precautions_alimentation || ''}
                          onChange={(e) => setSelectedHorse({...selectedHorse, precautions_alimentation: e.target.value})}
                          placeholder="Ex: Mange lentement, séparer du groupe..."
                          className="mt-1 bg-white"
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-red-700 font-medium">🚫 Intolérances / Allergies</Label>
                        <Textarea
                          value={selectedHorse.intolerances || ''}
                          onChange={(e) => setSelectedHorse({...selectedHorse, intolerances: e.target.value})}
                          placeholder="Ex: Allergie aux carottes, intolérance au blé..."
                          className="mt-1 bg-white border-red-200"
                          rows={2}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      {selectedHorse.detail_rations ? (
                        <div>
                          <p className="text-xs text-orange-700 font-medium mb-1">Rations:</p>
                          <p className="text-orange-900 whitespace-pre-line">{selectedHorse.detail_rations}</p>
                        </div>
                      ) : null}
                      {selectedHorse.precautions_alimentation ? (
                        <div className="p-3 bg-orange-100 rounded-lg">
                          <p className="text-xs text-orange-700 font-medium mb-1">⚠️ Précautions:</p>
                          <p className="text-orange-900">{selectedHorse.precautions_alimentation}</p>
                        </div>
                      ) : null}
                      {selectedHorse.intolerances ? (
                        <div className="p-3 bg-red-100 rounded-lg border border-red-200">
                          <p className="text-xs text-red-700 font-medium mb-1">🚫 Intolérances:</p>
                          <p className="text-red-900">{selectedHorse.intolerances}</p>
                        </div>
                      ) : null}
                      {!selectedHorse.detail_rations && !selectedHorse.precautions_alimentation && !selectedHorse.intolerances && (
                        <p className="text-center text-orange-600 py-2">Aucune information</p>
                      )}
                    </>
                  )}
                </div>
                )}
              </div>

              {/* Card 5: Cavaliers - Blue - Visible to instructors, owners, PP and DP (read-only for PP/DP) */}
              {(isInstructor() || isOwnerOrPPOrDP(selectedHorse)) && (
              <div className="rounded-xl bg-blue-50 border border-blue-200 overflow-hidden">
                {renderCollapsibleCardHeader("Cavaliers associés", Users, "cavaliers", "bg-blue-100", "text-blue-900", "border-blue-200", canManageCavaliers(selectedHorse))}
                {!collapsedCards.cavaliers && (
                <div className="p-3 sm:p-4">
                  {editingSection === 'cavaliers' && canManageCavaliers(selectedHorse) ? (
                    <div className="space-y-4">
                      {/* Propriétaire: Only editable by instructors */}
                      {isInstructor() ? (
                      <div>
                        <Label className="text-xs text-blue-800 font-medium">Propriétaire</Label>
                        <Select 
                          value={selectedHorse.proprietaire_id || (selectedHorse.is_club_horse ? 'club' : 'inconnu')}
                          onValueChange={(v) => {
                            if (v === 'inconnu') {
                              // Propriétaire inconnu - tout à null
                              setSelectedHorse({
                                ...selectedHorse, 
                                is_club_horse: false,
                                proprietaire_id: null,
                                type_pension: null,
                                tarif_pension_mensuel: null,
                                demi_pensionnaires: [],
                                proprietaires_partages: []
                              });
                            } else if (v === 'club') {
                              // Cheval du club - pas de pension
                              setSelectedHorse({
                                ...selectedHorse, 
                                is_club_horse: true,
                                proprietaire_id: null,
                                type_pension: null,
                                tarif_pension_mensuel: null,
                                proprietaires_partages: []
                              });
                            } else {
                              // Propriétaire sélectionné = cheval de propriétaire avec pension
                              setSelectedHorse({
                                ...selectedHorse, 
                                is_club_horse: false,
                                proprietaire_id: v,
                                demi_pensionnaires: []
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="bg-white mt-1"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inconnu">
                              <span className="flex items-center gap-2">
                                <HelpCircle className="w-4 h-4 text-gray-400" />
                                Inconnu
                              </span>
                            </SelectItem>
                            <SelectItem value="club">
                              <span className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-purple-600" />
                                Cheval du club
                              </span>
                            </SelectItem>
                            {cavaliers.filter(c => c.role_club === 'cavalier_proprio' || c.role_club === 'cavalier_proprietaire').map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      ) : (
                      /* Owner view: Read-only proprietaire display */
                      <div>
                        <Label className="text-xs text-blue-800 font-medium">Propriétaire</Label>
                        <div className="mt-1 p-2 bg-white rounded border border-blue-200">
                          <span className="text-sm text-blue-900">
                            {horseRiders?.proprietaire 
                              ? `${horseRiders.proprietaire.prenom} ${horseRiders.proprietaire.nom}` 
                              : 'Vous'}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">(non modifiable)</span>
                        </div>
                      </div>
                      )}
                      
                      {/* Si propriétaire connu: cavaliers PP uniquement (pension dans la carte suivante) */}
                      {selectedHorse.proprietaire_id && (
                        <>
                          {/* Cavaliers Propriétaires Partagés (PP) - Editable by instructor and owner */}
                          <div>
                            <Label className="text-xs text-blue-800 font-medium">Cavaliers Propriétaires Partagés (PP)</Label>
                            <p className="text-xs text-gray-500 mb-1">Ces cavaliers partagent la propriété du cheval</p>
                            <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2 bg-white">
                              {cavaliers.filter(c => c.role_club === 'cavalier_pp').length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-2">Aucun cavalier PP disponible</p>
                              ) : cavaliers.filter(c => c.role_club === 'cavalier_pp').map(c => (
                                <label key={c.id} className="flex items-center gap-2 p-2 hover:bg-blue-50 rounded cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selectedHorse.proprietaires_partages?.includes(c.id) || false}
                                    onChange={(e) => {
                                      const current = selectedHorse.proprietaires_partages || [];
                                      if (e.target.checked) {
                                        setSelectedHorse({...selectedHorse, proprietaires_partages: [...current, c.id]});
                                      } else {
                                        setSelectedHorse({...selectedHorse, proprietaires_partages: current.filter(id => id !== c.id)});
                                      }
                                    }}
                                    className="rounded text-amber-600"
                                  />
                                  <span className="text-sm">{c.prenom} {c.nom}</span>
                                  <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">PP</Badge>
                                </label>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* Si cheval du club: Cavaliers DP uniquement - Only instructors can edit DP */}
                      {selectedHorse.is_club_horse && isInstructor() && (
                        <div>
                          <Label className="text-xs text-blue-800 font-medium">
                            Cavaliers Demi-Pensionnaires (DP)
                            <span className="text-gray-500 font-normal ml-1">(max. 2)</span>
                          </Label>
                          <p className="text-xs text-gray-500 mb-1">Ces cavaliers paient un forfait mensuel pour monter ce cheval du club</p>
                          <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2 bg-white">
                            {cavaliers.filter(c => c.role_club === 'cavalier_dp').length === 0 ? (
                              <p className="text-sm text-gray-500 text-center py-2">Aucun cavalier DP disponible</p>
                            ) : cavaliers.filter(c => c.role_club === 'cavalier_dp').map(c => {
                              const isChecked = selectedHorse.demi_pensionnaires?.includes(c.id) || false;
                              const currentCount = (selectedHorse.demi_pensionnaires || []).length;
                              const isDisabled = !isChecked && currentCount >= 2;
                              
                              return (
                                <label 
                                  key={c.id} 
                                  className={`flex items-center gap-2 p-2 rounded cursor-pointer ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-50'}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    disabled={isDisabled}
                                    onChange={(e) => {
                                      const current = selectedHorse.demi_pensionnaires || [];
                                      if (e.target.checked && current.length < 2) {
                                        setSelectedHorse({...selectedHorse, demi_pensionnaires: [...current, c.id]});
                                      } else if (!e.target.checked) {
                                        setSelectedHorse({...selectedHorse, demi_pensionnaires: current.filter(id => id !== c.id)});
                                      }
                                    }}
                                    className="rounded text-purple-600"
                                  />
                                  <span className="text-sm">{c.prenom} {c.nom}</span>
                                  <Badge className="bg-purple-100 text-purple-700 text-xs ml-auto">DP</Badge>
                                </label>
                              );
                            })}
                          </div>
                          {(selectedHorse.demi_pensionnaires || []).length >= 2 && (
                            <p className="text-xs text-orange-600 mt-1">Maximum de 2 cavaliers atteint</p>
                          )}
                        </div>
                      )}
                      
                      {/* Propriétaire inconnu - message informatif */}
                      {!selectedHorse.proprietaire_id && !selectedHorse.is_club_horse && (
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-600 italic">
                            Propriétaire inconnu : aucune pension ni cavalier associé
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Affichage lecture seule */}
                      {selectedHorse.proprietaire_id ? (
                        <>
                          {/* Cheval de propriétaire */}
                          {horseRiders?.proprietaire && (
                            <div className="flex items-center gap-2 p-2 bg-blue-100 rounded-lg">
                              <User className="w-4 h-4 text-blue-700" />
                              <span className="text-sm font-medium text-blue-900">{horseRiders.proprietaire.prenom} {horseRiders.proprietaire.nom}</span>
                              <Badge className="bg-blue-200 text-blue-800 text-xs">Propriétaire</Badge>
                            </div>
                          )}
                          {/* Liste des PP - utiliser horseRiders au lieu de cavaliers */}
                          {horseRiders?.proprietaires_partages?.length > 0 && (
                            <>
                              <p className="text-xs text-gray-500 mt-2 mb-1">Propriétaires partagés (PP) :</p>
                              {horseRiders.proprietaires_partages.map(pp => (
                                <div key={pp.id} className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                                  <User className="w-4 h-4 text-amber-600" />
                                  <span className="text-sm text-amber-800">{pp.prenom} {pp.nom}</span>
                                  <Badge className="bg-amber-100 text-amber-700 text-xs">PP</Badge>
                                </div>
                              ))}
                            </>
                          )}
                          {(!horseRiders?.proprietaires_partages || horseRiders.proprietaires_partages.length === 0) && (
                            <p className="text-xs text-gray-500 italic pl-2">Aucun cavalier PP associé</p>
                          )}
                        </>
                      ) : selectedHorse.is_club_horse ? (
                        <>
                          {/* Cheval du club */}
                          <div className="flex items-center gap-2 p-2 bg-purple-100 rounded-lg">
                            <Building className="w-4 h-4 text-purple-700" />
                            <span className="text-sm font-medium text-purple-900">Cheval du club</span>
                          </div>
                          {/* Liste des DP - utiliser horseRiders au lieu de cavaliers */}
                          {horseRiders?.demi_pensionnaires?.length > 0 && (
                            <>
                              <p className="text-xs text-gray-500 mt-2 mb-1">Demi-pensionnaires (DP) :</p>
                              {horseRiders.demi_pensionnaires.map(dp => (
                                <div key={dp.id} className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-100">
                                  <User className="w-4 h-4 text-purple-600" />
                                  <span className="text-sm text-purple-800">{dp.prenom} {dp.nom}</span>
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">DP</Badge>
                                </div>
                              ))}
                            </>
                          )}
                          {(!horseRiders?.demi_pensionnaires || horseRiders.demi_pensionnaires.length === 0) && (
                            <p className="text-xs text-gray-500 italic pl-2">Aucun cavalier DP associé</p>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Propriétaire inconnu */}
                          <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
                            <HelpCircle className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-700">Propriétaire inconnu</span>
                          </div>
                          {/* Liste des PP même pour proprio inconnu */}
                          {horseRiders?.proprietaires_partages?.length > 0 && (
                            <>
                              <p className="text-xs text-gray-500 mt-2 mb-1">Propriétaires partagés (PP) :</p>
                              {horseRiders.proprietaires_partages.map(pp => (
                                <div key={pp.id} className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                                  <User className="w-4 h-4 text-amber-600" />
                                  <span className="text-sm text-amber-800">{pp.prenom} {pp.nom}</span>
                                  <Badge className="bg-amber-100 text-amber-700 text-xs">PP</Badge>
                                </div>
                              ))}
                            </>
                          )}
                          {(!horseRiders?.proprietaires_partages || horseRiders.proprietaires_partages.length === 0) && (
                            <p className="text-xs text-gray-500 italic pl-2">Aucun cavalier associé</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>
              )}
              {selectedHorse.proprietaire_id && (
                <div className="rounded-xl bg-purple-50 border border-purple-200 overflow-hidden">
                  {renderCollapsibleCardHeader("Pension & Tarification", Building, "pension", "bg-purple-100", "text-purple-900", "border-purple-200", isInstructor())}
                  {!collapsedCards.pension && (
                  <div className="p-3 sm:p-4">
                    {editingSection === 'pension' && isInstructor() ? (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-purple-800">Type de pension</Label>
                          <Select 
                            value={selectedHorse.type_pension || ''} 
                            onValueChange={(v) => {
                              const tarif = getPensionTarif(v);
                              setSelectedHorse({...selectedHorse, type_pension: v, tarif_pension_mensuel: tarif});
                            }}
                          >
                            <SelectTrigger className="bg-white mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                            <SelectContent>
                              {pensionTypes.map(p => (
                                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-purple-800">Tarif mensuel (€)</Label>
                          <Input
                            type="number"
                            value={selectedHorse.tarif_pension_mensuel || ''}
                            onChange={(e) => setSelectedHorse({...selectedHorse, tarif_pension_mensuel: parseFloat(e.target.value)})}
                            className="bg-white mt-1"
                          />
                        </div>
                        <Button className="btn-action w-full mt-2" onClick={handleUpdateHorse} data-testid="save-pension-btn">
                          Enregistrer
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-purple-700">Type:</span>
                          <span className="font-medium text-purple-900">
                            {pensionTypes.find(p => p.value === selectedHorse.type_pension)?.label || '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-purple-700">Tarif mensuel:</span>
                          <span className="font-semibold text-purple-900">
                            {selectedHorse.tarif_pension_mensuel ? `${selectedHorse.tarif_pension_mensuel}€` : '-'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              )}

              {/* Save button shown when editing any section */}
              {editingSection && editingSection !== 'pension' && (
                <Button className="btn-action w-full" onClick={handleUpdateHorse} data-testid="save-horse-btn">
                  Enregistrer les modifications
                </Button>
              )}
            </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-stone-700">
              Êtes-vous sûr de vouloir supprimer définitivement la fiche de <strong>{horseToDelete?.nom}</strong> ?
            </p>
            <p className="text-sm text-stone-500 mt-2">
              Cette action est irréversible. Toutes les données associées à ce cheval seront perdues.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteConfirmOpen(false);
                setHorseToDelete(null);
              }}
            >
              Annuler
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteHorse}
              data-testid="confirm-delete-horse-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de validation de commentaire */}
      <Dialog open={!!validationModal} onOpenChange={(open) => !open && setValidationModal(null)}>
        <DialogContent className="w-[90vw] max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              {validationModal?.commentaire?.type === 'attention' ? (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              ) : (
                <Eye className="w-5 h-5 text-orange-600" />
              )}
              {validationModal?.commentaire?.type === 'attention' ? 'Attention !' : 'À surveiller'}
            </DialogTitle>
          </DialogHeader>
          
          {validationModal && (
            <div className="space-y-3">
              <div className="p-3 bg-stone-50 rounded-lg border">
                <p className="text-sm font-medium text-stone-700 mb-1">
                  {validationModal.horse?.nom}
                </p>
                <p className="text-sm text-stone-600">{validationModal.commentaire?.content}</p>
                <p className="text-xs text-stone-500 mt-2">
                  Par {validationModal.commentaire?.created_by_name} • {validationModal.commentaire?.created_at ? format(new Date(validationModal.commentaire.created_at), 'dd/MM/yyyy HH:mm', { locale: fr }) : ''}
                </p>
              </div>
              
              <p className="text-sm text-stone-600">
                Maintenir le marqueur visible ?
              </p>
              
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleValidateComment(false)}
                >
                  <X className="w-4 h-4 mr-1" />
                  Désactiver
                </Button>
                <Button
                  size="sm"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleValidateComment(true)}
                >
                  <Check className="w-4 h-4 mr-1" />
                  Maintenir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal d'upload de médias pour le cheval */}
      {selectedHorse && (
        <UploadMediaModal
          open={mediaUploadOpen}
          onClose={() => setMediaUploadOpen(false)}
          onSuccess={() => {
            setMediaUploadOpen(false);
            toast.success('Médias ajoutés au cheval');
            fetchHorseMedias(selectedHorse.id);
          }}
          context={{ type: 'horse', horseId: selectedHorse.id }}
        />
      )}
      
      {/* Modal d'édition des médias du cheval */}
      {mediaToEdit && (
        <EditMediaModal
          open={horseEditModalOpen}
          onClose={() => {
            setHorseEditModalOpen(false);
            setMediaToEdit(null);
          }}
          media={mediaToEdit}
          horses={horses}
          activities={[]}
          onSuccess={() => {
            setHorseEditModalOpen(false);
            setMediaToEdit(null);
            fetchHorseMedias(selectedHorse?.id);
          }}
        />
      )}
      
      {/* Modal des réglages d'image du cheval */}
      {mediaToAdjust && (
        <ImageSettingsModal
          open={horseSettingsModalOpen}
          onClose={() => {
            setHorseSettingsModalOpen(false);
            setMediaToAdjust(null);
          }}
          media={mediaToAdjust}
          onSuccess={() => {
            setHorseSettingsModalOpen(false);
            setMediaToAdjust(null);
            fetchHorseMedias(selectedHorse?.id);
          }}
        />
      )}
    </div>
  );
};

// Component for assignment form
const AssignmentForm = ({ horse, cavaliers, currentProprio, currentDPs, onSave, onCancel }) => {
  const [selectedProprio, setSelectedProprio] = useState(currentProprio?.id || 'none');
  const [selectedDPs, setSelectedDPs] = useState(currentDPs.map(dp => dp.id));
  
  const proprios = cavaliers.filter(c => c.role_club === 'cavalier_proprio' || c.role_club === 'cavalier_proprietaire');
  const dps = cavaliers.filter(c => c.role_club === 'cavalier_dp');
  
  const handleAddDP = (dpId) => {
    if (dpId && selectedDPs.length < 2 && !selectedDPs.includes(dpId)) {
      setSelectedDPs([...selectedDPs, dpId]);
    }
  };
  
  const handleRemoveDP = (dpId) => {
    setSelectedDPs(selectedDPs.filter(id => id !== dpId));
  };
  
  const handleSave = () => {
    const proprioId = selectedProprio === 'none' ? null : selectedProprio;
    onSave(proprioId, selectedDPs);
  };
  
  return (
    <div className="space-y-4">
      {/* Propriétaire selection */}
      <div>
        <Label className="form-label">Propriétaire</Label>
        <Select value={selectedProprio} onValueChange={setSelectedProprio}>
          <SelectTrigger data-testid="select-proprio">
            <SelectValue placeholder="Sélectionner un propriétaire" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Aucun</SelectItem>
            {proprios.map(p => (
              <SelectItem key={p.id} value={p.id}>
                {p.pseudo || `${p.prenom} ${p.nom}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Demi-pensionnaires selection */}
      <div>
        <Label className="form-label">Demi-pensionnaires (max 2)</Label>
        {dps.filter(dp => !selectedDPs.includes(dp.id)).length > 0 && selectedDPs.length < 2 && (
          <Select 
            value="placeholder"
            onValueChange={handleAddDP}
          >
            <SelectTrigger data-testid="select-dp">
              <SelectValue placeholder="Ajouter un DP" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="placeholder" disabled>Sélectionner...</SelectItem>
              {dps.filter(dp => !selectedDPs.includes(dp.id)).map(dp => (
                <SelectItem key={dp.id} value={dp.id}>
                  {dp.pseudo || `${dp.prenom} ${dp.nom}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {selectedDPs.length >= 2 && (
          <p className="text-sm text-stone-500 mt-2">Maximum atteint (2 DP)</p>
        )}
        
        {/* Selected DPs */}
        <div className="mt-2 space-y-2">
          {selectedDPs.map(dpId => {
            const dp = cavaliers.find(c => c.id === dpId);
            return dp ? (
              <div key={dpId} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                <span className="text-sm">{dp.pseudo || `${dp.prenom} ${dp.nom}`}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleRemoveDP(dpId)}
                  className="h-6 w-6 p-0 text-red-500"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : null;
          })}
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Annuler
        </Button>
        <Button 
          className="btn-primary flex-1"
          onClick={handleSave}
          data-testid="save-assignment-btn"
        >
          Enregistrer
        </Button>
      </div>
    </div>
  );
};
